import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { bearerHeaders } from './api.ts';

// Göm Capacitor SplashScreen när appen är redo
function hideSplash() {
  try {
    const cap = (window as any).Capacitor;
    if (cap?.Plugins?.SplashScreen) {
      cap.Plugins.SplashScreen.hide();
    }
  } catch (_) {}
}
import L from 'leaflet';
import QRCode from 'qrcode';
import { api, auth, huntDisplayName, resolveUrl, qmeetApi, QidUser, HuntListing, Booking, Mark, Jaktlag, JaktlagMember, Jakt, Post, Observation, Avskjutning, AvskjutningsMal, DashboardStats, UserEquipment, MarkMember, MarkGroup, MarkGroupPermissions, MarkCalendarEvent, MarkChatMessage, MarkShootingPlanItem, MarkMeatSharingItem, MarkMeatQueueItem, MarkAlbumImage, MarkRolePermissions, MarkApplication, ApplicationContract, ContractTemplate, MarkRequirements, DispensationRequest, MarkCostModel, FallAvgift, KottKostnad, MarkInviteLink, MarkViltRegistration, ViltHandlingLog, JaktklubAd, JaktklubApplication, JaktklubSettings } from './api.ts';
import { KartaTab } from './KartaTab';
import { initWatchBridge, clearWatchCredentials } from './watchBridge';
import { ChatList, OPEN_CHATS_EVENT, CHATS_CHANGED_EVENT, useUnreadChats } from './components/chat/ChatList';
import { initPush, askForPush, forgetPushDevice, setOpenChat, openActionFor, PUSH_OPEN_EVENT, OPEN_MARK_TAB_EVENT, type PushTarget } from './push';
import { finishHuntWorkout } from './hunterHealth';
import { finishHuntWindow, rememberHuntWindow, startHuntMediaAutoSync } from './huntMediaSync';
import { MedlemmarTab } from './mark-tabs/MedlemmarTab';
import { KalenderTab } from './mark-tabs/KalenderTab';
import { GruppchatTab } from './mark-tabs/GruppchatTab';
import { AvskjutningsplanTab } from './mark-tabs/AvskjutningsplanTab';
import { KottdelningTab } from './mark-tabs/KottdelningTab';
import { AlbumTab } from './mark-tabs/AlbumTab';
import { KontraktTab } from './mark-tabs/KontraktTab';
import { GroupPrices } from './mark-tabs/GroupPrices';
import { MarkInfoTab } from './mark-tabs/MarkInfoTab';
import { AdsTab } from './mark-tabs/AdsTab';
import { EkonomiTab } from './mark-tabs/EkonomiTab';
import { EfterJaktTab } from './mark-tabs/EfterJaktTab'; // P4
import { HuntPayments, ForbiddenGamePanel } from './mark-tabs/HuntPayments'; // P5, A6
import { StatistikTab } from './mark-tabs/StatistikTab'; // P6
import { huntApi, notifyHuntEconomyChanged, kr as krText, EFTERJAKT_FOCUS_KEY } from './huntEconomy'; // P4
import { AdmissionsPanel } from './mark-tabs/AdmissionsPanel';
import { LegacyApplications } from './mark-tabs/LegacyApplications';
import { PublicSigningPage, signingTokenFromPath } from './pages/SigningPage';
import { StripeConnectPanel } from './mark-tabs/StripeConnectPanel';
import { LodgingSettings } from './mark-tabs/LodgingSettings';
import { peekPendingPurchase, PURCHASE_UPDATED_EVENT } from './views/jakt/adPurchase';
import { MapMeeting, ActiveMeeting } from './MapMeeting';
import { LabelPrinter, LabelData as LabelDataType, generateLabels as generateLabelsFn, DEFAULT_VILT_CUTS } from './LabelPrinter';
import { ApplyButton, MyContracts, ContractTab, LegalContractDocument, GastjaktsAvtal, PubliceringsAvtal, PubliceringsAvtalModal, OwnerSigningPanel } from './ApplicationSystem';
import PlaneraJakt from './views/planera/PlaneraJakt';
import { InvitationDialog } from './views/inbjudan/InvitationDialog';
import { InvitationPage } from './views/inbjudan/HuntInvitation';
import { DARK_THEME, LIGHT_THEME, ThemeCtx, ThemeMode, getTheme, useThemeMode, FONT_UI, RADIUS, themeCssVars } from './theme';
import { planSpeciesFamilyKey } from './swedishHuntingLegality';
import ArrendeChat from './ArrendeChat';
import { resolveLivekitWsUrl } from './livekit-ws';
import { SUBCAT_MAP as WD_SM, FAMILY_DISPLAY_NAMES as WD_FL, SPECIES as WD_SPECIES, SUBCATS as WD_SUBCATS } from './wildlifeData.ts';
import type {
  View, CartItem,
  GuestAdCriteria, GuestApplication, GuestAd, PreviousGuest,
  AiResult, ChatMsg, TranscriptEntry, HuntObsRow, HuntKillRow,
  MdTab, WizardData, FastighetStatus,
  MeatProduct,
  DogEntry, EquipmentEntry, WeaponLicenseEntry, CropState, WeaponEntry, LoanFormData, ProfileState,
} from './types';
import {
  Loader, Empty, Bdg,
  SignaturePad,
  LoginModal,
  HuntCard,
  MarkCard, markImg,
  MeatCard,
  CartSidebar,
  TopNav,
  MktTopBar,
  AnimatedPlaceholder,
  IntegrationStatusBadge,
  GREEN_PLACEHOLDER, fmtD, fmtSEK,
} from './components';
import { NAV_MAIN_H, NAV_TABS_H } from './components/TopNav';
import { BottomNav, BOTTOM_NAV_SPACE, type BottomActive } from './components/BottomNav';
import { useNavState, setOpenMark, pickMarkId, lastMarkId, getNavState, requestHuntAction, takeHuntAction } from './nav/navState';
import { hasOwnMarks, initialMpTab, isPlainStart, rememberedMinJakt, rememberMinJakt } from './nav/startPage';
import { QPanel } from './q/QPanel';
import { Q_CONVERSATION } from './q/qClient';
import { onQLaunch, startQLaunch } from './q/qLaunch';
import { deviceContext } from './q/qField';
import { Icon, IconButton, Button, Field, Input, useIsMobile, type TabItem } from './components/ui';
import { BrandLogo } from './components/BrandLogo';
import { GuestJoin, loadGuestWait, saveGuestWait, type GuestWait } from './components/GuestJoin';
import { guestApi, GuestApiError, setGuestHunt, type GuestApproved } from './api.ts';
import { X } from 'lucide-react';
import { MarkHeader, orderMarkTabs } from './views/minjakt/MarkHeader';
import { UpcomingCard } from './views/minjakt/parts';
import { DemoMarkBanner, isDemoMark } from './views/minjakt/DemoMark';
import { OAuthConsentModal } from './components/McpConnections';
import { StatusBar, Style } from '@capacitor/status-bar';
import { isShotEntry } from './huntTranscriptInsights';
import { VDashboard, VJaktlag, VJakter, VAvskjutning, VMal, VMarks, VMarketplace, VProfil, VAdmin, VInstallningar } from './views';
import { parseHuntTranscript, parseTranscriptEntries, QCARNE_URL } from './views/VMarketplace';
import { ImagePickerModal, DraggablePositionMap, EditListingModal, EditMarkModal, MeatFormModal, CompletedHuntModal, AppContractFlow, HuntMap, MeatMap, HeroSearch, FastighetMiniMap, MarkRegistrationWizard, DetailModal, MarkDetailModal } from './modals';
import { apiFetch } from './appFetch';
import { LabelView } from './LabelView';
import { labelIdFromUrl } from './labelLayout';
import { IntegrationPanel } from './panels';
import { ViltPublicPage } from './pages/ViltPublicPage';
import { LegalPage, legalDocForPath } from './pages/LegalPage';
import { SupportPage, isSupportPath } from './pages/SupportPage';
import { ModerationNotice } from './components/moderation/ModerationNotice';
import { InviteCodeGate } from './components/invite/InviteCodeGate';
import { InviteRequiredError, inviteCodePending, rememberMarkInvite } from './components/invite/inviteRequired';
import { initAndroidBackButton, onAndroidBack } from './androidApp';

const GUEST_ROLES=[{k:'skytt',l:'Skytt'},{k:'hundforare',l:'Hundförare'},{k:'eftersok',l:'Eftersöksjägare'}];

async function gqlFetch(query:string,variables?:Record<string,unknown>){
  const token=localStorage.getItem('qvenari_access_token');
  const r=await fetch(resolveUrl('/graphql'),{method:'POST',headers:{'Content-Type':'application/json','X-Api-Key':'dev-secret',...bearerHeaders(token)},body:JSON.stringify({query,variables})});
  const j=await r.json();if(j.errors)throw new Error(j.errors[0].message);return j.data;
}

let T = DARK_THEME;

const GT = ['Alla','älg','dovhjort','kronhjort','rådjur','vildsvin','fasan','kanadagås'];
const GL: Record<string,string> = { Alla:'Alla jakter', älg:'Älg', dovhjort:'Dovhjort', kronhjort:'Kronhjort', rådjur:'Rådjur', vildsvin:'Vildsvin', fasan:'Fasan', kanadagås:'Kanadagås' };
const GE: Record<string,string> = {};
const DC: Record<string,string> = { Nybörjare:'#27ae60', Medel:T.amber, Erfaren:'#e74c3c' };

// Map primary_game_type to animal photo
/** Avsluta jakten via servern; okänd jakt (servern nåddes inte vid start) avslutas direkt i Qmeet. */
function endHuntMeeting(meetingId:string){
  api.endMarkHunt(meetingId).catch(()=>{
    fetch(qmeetApi(`/api/meetings/${meetingId}/end`),{method:'POST',headers:{'Content-Type':'application/json',...bearerHeaders()},body:'{}'}).catch(()=>{});
  });
}
function animalImg(listing: HuntListing): string {
  if (listing.imageUrl) return resolveUrl(listing.imageUrl);
  return GREEN_PLACEHOLDER;
}

const NAV: {k:View;l:string}[] = [
  {k:'marketplace',l:'Marketplace'},{k:'dashboard',l:'Dashboard'},
  {k:'marks',l:'Jaktmarker'},{k:'jaktlag',l:'Jaktlag'},
  {k:'jakter',l:'Jakter'},{k:'avskjutning',l:'Avskjutning'},
  {k:'mal',l:'Mål'},{k:'profil',l:'Jägarprofil'},{k:'etiketter',l:'Etiketter'},
];

function fmtP(p:number,u:string){return`${p.toLocaleString('sv-SE')} kr ${u}`}
function spotsC(a:number,t:number){const r=a/t;if(r<=0)return T.danger;if(r<=0.25)return'#e67e22';return T.success}
function stC(s:string){switch(s.toLowerCase()){case'aktiv':case'active':case'pågående':return T.success;case'planerad':case'planned':case'pending':return T.amber;case'avslutad':case'completed':case'done':return T.textMuted;case'on_track':return T.success;case'behind':return T.danger;default:return T.textSub}}
function stL(s:string){switch(s.toLowerCase()){case'aktiv':case'active':return'Aktiv';case'planerad':case'planned':case'pending':return'Planerad';case'avslutad':case'completed':case'done':return'Avslutad';case'on_track':return'I fas';case'behind':return'Efter';default:return s}}

const iS:React.CSSProperties={width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:4,padding:'10px 14px',color:T.text,fontSize:14,outline:'none',boxSizing:'border-box',fontFamily:'inherit'};
const lS:React.CSSProperties={display:'block',color:T.textSub,fontSize:11,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:6};
const bP:React.CSSProperties={background:T.amber,color:'#000',border:'none',borderRadius:5,padding:'12px 24px',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'inherit'};
const bSc:React.CSSProperties={background:T.surface,color:T.text,border:`1px solid ${T.border}`,borderRadius:5,padding:'12px 24px',fontSize:14,cursor:'pointer',fontFamily:'inherit'};
const cS:React.CSSProperties={background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:20};
const secT:React.CSSProperties={color:T.textMuted,fontSize:11,fontWeight:600,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:16};




// ── HUNT CARD ─────────────────────────────────────────────────────


// ── MARK CARD ─────────────────────────────────────────────────────



// ── MARK DETAIL MODAL ──────────────────────────────────────────────
// MarkDetailModal extracted to ./modals/MarkDetailModal

// KontraktTab replaced by ContractTab from ApplicationSystem.tsx
// ── MY MARK DETAIL VIEW ──────────────────────────────────────────

/** Flikar som MyMarkDetailView kan visa; allt annat blir Karta. */
const knownMdTab=(t:string|null|undefined):MdTab|null=>t&&(MD_TABS.some(x=>x.k===t)||t==='annons'||t==='annonser')?t as MdTab:null;
const MD_TABS:{k:MdTab;l:string;w:number}[]=[{k:'karta',l:'Karta',w:56},{k:'medlemmar',l:'Medlemmar',w:100},{k:'kalender',l:'Kalender',w:82},{k:'gruppchat',l:'Chatt',w:90},{k:'album',l:'Album',w:60},{k:'avskjutningsplan',l:'Avskjutningsplan',w:140},{k:'kottdelning',l:'Viltkött',w:74},{k:'ansokningar',l:'Ansökningar',w:104},{k:'kontrakt',l:'Kontrakt',w:88},{k:'ekonomi',l:'Ekonomi',w:80},{k:'efterjakt',l:'Efter jakt',w:90},{k:'statistik',l:'Statistik',w:80},{k:'integrationer',l:'Integrationer',w:110}];

// Global hunt state — persists across MyMarkDetailView re-mounts (e.g. when navigating away and back)
const _globalHunt:{current:{markId:string;session:{roomName:string;livekitUrl:string;livekitToken:string;title:string;meetingId:string;calEventId?:string}}|null}={current:null};

export function MyMarkDetailView({mark:markProp,user,onBack,onDelete,isAdmin:isAdminProp,onCalEventCreated,pendingJoinMeetingId,userAvatarUrl,onJaktklubSettingsChanged,initialTab,initialGroupId,initialAdId,onOpenPublicPage,onMarkImageChanged,tabRequest,huntMarks,mapFullscreenRequest}:{mark:Mark;user:QidUser|null|undefined;onBack:()=>void;/** Den publika jaktsidan (bara publicerade marker). */onOpenPublicPage?:()=>void;/** Markens bild ändrades via en annons (B11). */onMarkImageChanged?:(m:Mark)=>void;onDelete?:(id:string)=>void;isAdmin?:boolean;onCalEventCreated?:(ev:MarkCalendarEvent)=>void;pendingJoinMeetingId?:string|null;userAvatarUrl?:string|null;onJaktklubSettingsChanged?:()=>void;/** Öppna en viss flik direkt, t.ex. Ekonomi från en annons. */initialTab?:MdTab;/** Ekonomi: gruppen vars priser ska visas. */initialGroupId?:string|null;/** Annonser: annonsen som ska öppnas. */initialAdId?:string|null;/** Ändras när initialTab ska visas igen, t.ex. efter tryck på en pushnotis. */tabRequest?:number;/** UX4: markerna man kan starta/planera jakt på (jaktknappen på kartan). */huntMarks?:{id:string;name:string}[];/** UX11: bottenmenyns Karta – kartan i helskärm (nytt nummer per tryck). */mapFullscreenRequest?:number}){
  const[mark,setMark]=useState<Mark>(markProp);
  // AS1a: demomarken visas som för en gäst, även för sajtadmin (API:t avvisar ändringar).
  const isDemo=isDemoMark(markProp);
  const isAdmin=isAdminProp&&!isDemo;
  const isMarkOwner=!!(
    (user?.sub && mark.ownerUserId && user.sub === mark.ownerUserId) ||
    (user?.email && mark.contactEmail && user.email.toLowerCase() === mark.contactEmail.toLowerCase())
  );
  // Okänd flik (t.ex. från en notis till en äldre/nyare app) ger Karta i stället för en tom sida.
  const[tab,setTab]=useState<MdTab>(knownMdTab(initialTab)??'karta');
  const mapFsHandledRef=useRef<number|undefined>(undefined);
  useEffect(()=>{if(tabRequest&&initialTab)setTab(knownMdTab(initialTab)??'karta');},[tabRequest]); // eslint-disable-line react-hooks/exhaustive-deps
  // Bottenmenyn (BottomNav) markerar Karta/Chatt och öppnar den här marken nästa gång.
  useEffect(()=>{setOpenMark({id:markProp.id,tab});},[markProp.id,tab]);
  useEffect(()=>()=>setOpenMark(null),[]);
  // Push: ingen notis om gruppchatten medan den är öppen här (push.ts).
  useEffect(()=>{
    if(tab!=='gruppchat')return;
    setOpenChat(`mark:${markProp.id}`);
    return()=>setOpenChat(null);
  },[tab,markProp.id]);
  const mob=useIsMobile();
  /** Gruppen som markeras på Ekonomi-sidan (länk från en annons). */
  const[ekonomiFocus,setEkonomiFocus]=useState<string|null>(initialGroupId??null);
  const openEkonomi=(groupId?:string|null)=>{setEkonomiFocus(groupId??null);setTab('ekonomi');window.scrollTo({top:0});};
  useEffect(()=>{window.scrollTo({top:0,behavior:'instant' as ScrollBehavior});},[]);
  const[members,setMembers]=useState<MarkMember[]>([]);const[membersLd,setMembersLd]=useState(false);
  const[groups,setGroups]=useState<MarkGroup[]>([]);const[groupsLoaded,setGroupsLoaded]=useState(false);
  const[expandedGroup,setExpandedGroup]=useState<string|null>(null);
  const[newGroupName,setNewGroupName]=useState('');const[newGroupColor,setNewGroupColor]=useState('#EE8F09');const[showNewGroupForm,setShowNewGroupForm]=useState(false);
  const[editingGroupId,setEditingGroupId]=useState<string|null>(null);const[editGroupName,setEditGroupName]=useState('');const[editGroupColor,setEditGroupColor]=useState('');
  const[editingGroupPermsId,setEditingGroupPermsId]=useState<string|null>(null);const[editGroupPerms,setEditGroupPerms]=useState<MarkGroupPermissions|null>(null);
  const[editingJaktklubId,setEditingJaktklubId]=useState<string|null>(null);const[editJaktklub,setEditJaktklub]=useState<Pick<JaktklubSettings,'maxJakttillfallen'|'description'|'kontaktEmail'|'arsKostnad'>|null>(null);
  const[jaktklubApps,setJaktklubApps]=useState<Record<string,JaktklubApplication[]>>({});const[jaktklubAppsLoaded,setJaktklubAppsLoaded]=useState<Record<string,boolean>>({});
  const[events,setEvents]=useState<MarkCalendarEvent[]>([]);const[eventsLd,setEventsLd]=useState(false);
  const[messages,setMessages]=useState<MarkChatMessage[]>([]);const[messagesLd,setMessagesLd]=useState(false);
  // CH1: chatten är läst så länge den är öppen – vid öppning och när nya meddelanden kommer.
  const chatSeen=messages.length?messages[messages.length-1].id:'';
  useEffect(()=>{
    if(tab!=='gruppchat'||!user)return;
    api.markChatRead(`mark:${markProp.id}`).then(()=>window.dispatchEvent(new Event(CHATS_CHANGED_EVENT))).catch(()=>{});
  },[tab,markProp.id,chatSeen,user]);
  const[plan,setPlan]=useState<MarkShootingPlanItem[]>([]);const[planLd,setPlanLd]=useState(false);
  const[planSeason,setPlanSeason]=useState<string>(()=>{const m=new Date().getMonth();const y=new Date().getFullYear();return m>=6?`${y}/${y+1}`:`${y-1}/${y}`;});
  const[planCreating,setPlanCreating]=useState(false);
  const[meatItems,setMeatItems]=useState<MarkMeatSharingItem[]>([]);const[meatLd,setMeatLd]=useState(false);
  const[queue,setQueue]=useState<MarkMeatQueueItem[]>([]);const[queueLd,setQueueLd]=useState(false);
  const[viltRegs,setViltRegs]=useState<MarkViltRegistration[]>([]);
  const[falltViltPins,setFalltViltPins]=useState<any[]>([]);
  // Viltkött UI state
  const[viltDispModal,setViltDispModal]=useState<{reg:MarkViltRegistration;step:'choose'|'list_share'|'for_sale'|'to_shooter'}|null>(null);
  const[viltShareCount,setViltShareCount]=useState(2);
  const[viltAvpalsat,setViltAvpalsat]=useState(false);
  const[viltSlaktvikt,setViltSlaktvikt]=useState('');
  const[viltTrikin,setViltTrikin]=useState(false);
  const[viltCesium,setViltCesium]=useState(false);
  const[viltSaleNotes,setViltSaleNotes]=useState('');
  const[viltFallavgift,setViltFallavgift]=useState('');
  const[viltKottkostnad,setViltKottkostnad]=useState('');
  const[viltVikt,setViltVikt]=useState('');
  const[showAddVilt,setShowAddVilt]=useState(false);
  const[addViltForm,setAddViltForm]=useState<{gameType:string;shotDate:string;shotByName:string;shotByEmail:string;sex:string;ageClass:string;estimatedWeightKg:string;notes:string}>({gameType:'Älg',shotDate:new Date().toISOString().slice(0,10),shotByName:'',shotByEmail:'',sex:'',ageClass:'',estimatedWeightKg:'',notes:''});
  const[viltSaving,setViltSaving]=useState(false);
  const[handlingLogModal,setHandlingLogModal]=useState<MarkViltRegistration|null>(null);
  const[handlingLog,setHandlingLog]=useState<ViltHandlingLog[]>([]);
  const[handlingLogLd,setHandlingLogLd]=useState(false);
  const[viltQrUrls,setViltQrUrls]=useState<Record<string,string>>({});
  // Album
  const[albumImages,setAlbumImages]=useState<MarkAlbumImage[]>([]);const[albumLd,setAlbumLd]=useState(false);
  const[albumViewer,setAlbumViewer]=useState<number|null>(null);
  // Label printer embedded in köttdelning
  const[showLabelPrinter,setShowLabelPrinter]=useState(false);
  const[labelPrintReg,setLabelPrintReg]=useState<MarkViltRegistration|null>(null);
  // Publiceringsavtal modal
  const[showPubAvtal,setShowPubAvtal]=useState(false);
  const[pubAvtalSigned,setPubAvtalSigned]=useState(false);
  const[pubAvtalSignName,setPubAvtalSignName]=useState('');
  // Snabbstart jakt — LiveKit direct integration
  const[sbLoading,setSbLoading]=useState(false);
  const[sbError,setSbError]=useState('');
  const[huntSession,setHuntSession]=useState<{roomName:string;livekitUrl:string;livekitToken:string;title:string;meetingId:string;calEventId?:string}|null>(()=>{
    // Restore active hunt if user navigated away and came back to this mark
    const gh=_globalHunt.current;
    return (gh&&gh.markId===markProp.id)?gh.session:null;
  });
  // Sync huntSession to global ref so it survives re-mounts
  useEffect(()=>{
    if(huntSession){_globalHunt.current={markId:mark.id,session:huntSession};}
    else if(_globalHunt.current?.markId===mark.id){_globalHunt.current=null;}
  },[huntSession]); // eslint-disable-line
  // EJ7: kartan lämnades strax efter jaktstart – syns i Xcode-konsolen om fliken byts under en jakt.
  useEffect(()=>{if(huntSession&&tab!=='karta')console.warn('[QVENARI-FS] fliken bytt under jakten:',tab);},[tab]); // eslint-disable-line react-hooks/exhaustive-deps
  // Auto-switch to karta tab if restoring an active hunt on mount
  useEffect(()=>{
    if(_globalHunt.current?.markId===mark.id&&_globalHunt.current.session){setTab('karta');}
  },[]); // eslint-disable-line
  // Auto-join pending meeting from link (?join=MEETING_ID&mark=MARK_ID)
  useEffect(()=>{
    if(!pendingJoinMeetingId)return;
    setTab('karta');
    // Call handleJoinFromChat after short delay to let KartaTab mount
    setTimeout(()=>{try{handleJoinFromChat(pendingJoinMeetingId);}catch(e){console.error('[JOIN] handleJoinFromChat crash:',e);}},400);
  },[pendingJoinMeetingId]); // eslint-disable-line
  const[completedHuntDetail,setCompletedHuntDetail]=useState<MarkCalendarEvent|null>(null);
  const[calDetailEvent,setCalDetailEvent]=useState<MarkCalendarEvent|null>(null);
  const[showUpdateGoals,setShowUpdateGoals]=useState(false);
  const huntStartTimeRef=React.useRef<string|null>(null);
  async function handleSnabbstartMark():Promise<{roomName:string;livekitUrl:string;livekitToken:string;title:string;meetingId:string}|null>{
    if(sbLoading)return null;
    if(isDemo){setSbError('Demomarken går bara att titta på – där kan ingen jakt startas.');return null;}
    setSbError('');setSbLoading(true);
    const IS_CAP = !!(window as any).Capacitor?.isNativePlatform?.();
    try{
      // Servern skapar Qmeet-mötet, kopplar det till marken och meddelar gruppchatten.
      const res=await api.startMarkHunt(mark.id);
      if(!res.livekitToken)throw new Error('Ingen radio-token från servern');
      // Snabbstartad jakt skapar INTE kalender-event eller gästjaktannons — bara planerade jakter gör det
      const calEventId:string|undefined = undefined;
      huntStartTimeRef.current = new Date().toISOString();
      const livekitUrl = resolveLivekitWsUrl({ livekit_url: res.livekitUrl ?? undefined }, IS_CAP);
      const session={roomName:res.hunt.meetingId,livekitUrl,livekitToken:res.livekitToken,title:res.hunt.title,meetingId:res.hunt.meetingId,calEventId};
      setTab('karta');
      setHuntSession(session);
      void askForPush(); // jaktchatten och nästa jakt på marken ska kunna nå telefonen
      setSbLoading(false);
      return session;
    }catch(e:unknown){
      console.error('[QVENARI-MEET] ERROR — handleSnabbstartMark misslyckades:', e);
      setSbError((e as Error).message||'Kunde inte starta möte. Kontrollera att Qmeet körs.');setSbLoading(false);return null;
    }
  }
  // Join hunt from chat message — fetches token and switches to Karta tab (fullscreen auto-triggered by KartaTab)
  async function handleJoinFromChat(meetingId:string){
    const isCapJoin=!!(window as any).Capacitor?.isNativePlatform?.();
    try{
      const res=await api.joinMarkHunt(mark.id,meetingId);
      if(!res.livekitToken)throw new Error('Ingen radio-token från servern');
      const lkUrl = resolveLivekitWsUrl({ livekit_url: res.livekitUrl ?? undefined }, isCapJoin);
      setHuntSession({roomName:meetingId,livekitUrl:lkUrl,livekitToken:res.livekitToken,title:res.hunt.title,meetingId});
      setTab('karta');
      void askForPush();
    }catch(e:unknown){
      // Om mötet saknas eller redan avslutats (t.ex. stängt i Qmeet) — starta en ny jakt på marken
      const msg=(e as Error).message||'';
      if(msg==='Jakten har avslutats'||msg==='Jakten finns inte'){
        console.log('[QVENARI-MEET] Meeting not joinable — starting new hunt for mark', {meetingId,msg});
        await handleSnabbstartMark();
        return;
      }
      alert(msg||'Kunde inte ansluta till jakten');
    }
  }
  // Invite form
  const[invEmail,setInvEmail]=useState('');const[invName,setInvName]=useState('');const[invRole,setInvRole]=useState('jagare');const[invBusy,setInvBusy]=useState(false);const[myPerms,setMyPerms]=useState<MarkRolePermissions|null>(null);
  // Invite links
  const[invLinks,setInvLinks]=useState<MarkInviteLink[]>([]);const[invLinksLd,setInvLinksLd]=useState(false);const[ilLabel,setIlLabel]=useState('');const[ilRole,setIlRole]=useState('jagare');const[ilMaxUses,setIlMaxUses]=useState(1);const[ilExpires,setIlExpires]=useState('');const[ilBusy,setIlBusy]=useState(false);const[ilCopied,setIlCopied]=useState<string|null>(null);const[ilShowForm,setIlShowForm]=useState(false);
  // Calendar form
  const[calTitle,setCalTitle]=useState('');const[calType,setCalType]=useState('hunt');const[calStart,setCalStart]=useState('');const[calEnd,setCalEnd]=useState('');const[calLoc,setCalLoc]=useState('');const[calBusy,setCalBusy]=useState(false);
  const[calInvites,setCalInvites]=useState<{email:string;name:string;inviteType:string}[]>([]);const[calQmeet,setCalQmeet]=useState(true);const[guestEmail,setGuestEmail]=useState('');const[guestName,setGuestName]=useState('');
  const[showSatPlanerare,setShowSatPlanerare]=useState(false);const[calSatPlan,setCalSatPlan]=useState<unknown>(undefined);const[planEditEvent,setPlanEditEvent]=useState<MarkCalendarEvent|null>(null);
  // Guest hunt ads
  const[guestAdEventId,setGuestAdEventId]=useState<string|null>(null);
  const[guestAds,setGuestAds]=useState<GuestAd[]>([]);
  const[prevGuests,setPrevGuests]=useState<PreviousGuest[]>([]);
  const[pendingApplicants,setPendingApplicants]=useState<{email:string;name:string;id:string}[]>([]);
  const[guestAdBusy,setGuestAdBusy]=useState(false);
  const[editAdRole,setEditAdRole]=useState<string|null>(null);
  const[adForm,setAdForm]=useState<{roleType:string;spotsTotal:number;pricePerJakt:number;requiresWeapon:boolean;requiresDog:boolean;requiresEftersok:boolean;minExperience:number;customText:string}>({roleType:'skytt',spotsTotal:1,pricePerJakt:0,requiresWeapon:false,requiresDog:false,requiresEftersok:false,minExperience:0,customText:''});
  // Chat
  const[chatMsg,setChatMsg]=useState('');const[chatBusy,setChatBusy]=useState(false);
  const chatEndRef=useRef<HTMLDivElement>(null);
  const chatContainerRef=useRef<HTMLDivElement>(null);
  const calendarRef=useRef<HTMLDivElement>(null);
  // Chat sync (WhatsApp / Telegram)
  const[chatSyncOpen,setChatSyncOpen]=useState<'whatsapp'|'telegram'|'sms'|null>(null);
  const[chatInts,setChatInts]=useState<any[]>([]);
  const[chatWaQR,setChatWaQR]=useState<string|null>(null);
  const[chatTgToken,setChatTgToken]=useState('');
  const[chatTgChatId,setChatTgChatId]=useState('');
  const[chatTgBusy,setChatTgBusy]=useState(false);
  const[chatWaGroupBusy,setChatWaGroupBusy]=useState(false);
  const[chatWaPhoneInput,setChatWaPhoneInput]=useState<string|null>(null);  // null=hidden, string=shown
  const chatWaPollRef=useRef<number|null>(null);
  const[tgBotInfo,setTgBotInfo]=useState<{username:string|null,firstName:string|null}|null>(null);
  // Supertext SMS state
  const[smsBusy,setSmsBusy]=useState(false);
  const[smsApiUser,setSmsApiUser]=useState('');
  const[smsApiPass,setSmsApiPass]=useState('');
  const[smsPhone,setSmsPhone]=useState('');
  const[smsResult,setSmsResult]=useState<{message:string;success:boolean}|null>(null);
  // Shooting plan edit
  const[editPlanId,setEditPlanId]=useState<string|null>(null);const[editPlanned,setEditPlanned]=useState(0);const[editCost,setEditCost]=useState(0);
  const[planEditMode,setPlanEditMode]=useState(false);
  const[planDraft,setPlanDraft]=useState<Record<string,{planned:number;cost:number}>>({});
  const[newSpeciesName,setNewSpeciesName]=useState('');
  const[planSaving,setPlanSaving]=useState(false);
  // Species population estimates (for avskjutningsplan generation)
  // speciesOccurrenceJson uses Swedish keys ('älg','rådjur'); normalize to planSpeciesFamilyKey format ('alg','radjur')
  const POP_FK_TO_OCC:Record<string,string>={alg:'älg',radjur:'rådjur',bjorn:'björn',rav:'räv',gravling:'grävling',baver:'bäver',mardhund:'mårdhund',kanadagas:'kanadagås'};
  const[popLevels,setPopLevels]=useState<Record<string,number>>(()=>{
    try{
      const raw=JSON.parse(mark.speciesOccurrenceJson||'{}') as Record<string,number>;
      const norm:Record<string,number>={};
      for(const[k,v] of Object.entries(raw)) norm[planSpeciesFamilyKey(k)]=v;
      return norm;
    }catch{return {}}
  });
  const[popCounts,setPopCounts]=useState<Record<string,number>>({});
  const savePopLevels=(newLvs:Record<string,number>)=>{
    const occSave:Record<string,number>={};
    for(const[k,v] of Object.entries(newLvs)) occSave[POP_FK_TO_OCC[k]??k]=v;
    const json=JSON.stringify(occSave);
    api.updateMark(mark.id,{speciesOccurrenceJson:json}).then((m:any)=>{setMark(m);setAnnonsSpecOcc(occSave);}).catch(()=>{});
  };
  // Generate plan
  const[showGenPlan,setShowGenPlan]=useState(false);
  type GenRow={gameType:string;count:number;cost:number;included:boolean};
  const[genRows,setGenRows]=useState<GenRow[]>([]);
  const[genSaving,setGenSaving]=useState(false);
  // Subcats to exclude from plan display and generation (only kapital and 4-taggare; kalv stays)
  const EXCL_SUBCAT=(gt:string)=>{const l=gt.toLowerCase();return l.includes('kapital')||l.includes('4-taggare');};
  const buildPopPlan=()=>{
    const ha=Number((mark as any).areaHa)||100;
    const LF=[0,0.25,0.5,1.0,2.0,4.0];
    const rows:GenRow[]=[];
    for(const sp of WD_SPECIES){
      if(sp.refPer100Ha===0&&!popCounts[sp.key])continue;
      const lv=popLevels[sp.key]||0;
      const totalPop=popCounts[sp.key]||(lv>0?Math.round((ha/100)*sp.refPer100Ha*LF[lv]):Math.round((ha/100)*sp.refPer100Ha));
      if(!totalPop)continue;
      const totalHarvest=Math.max(1,Math.round(totalPop*sp.harvFrac));
      const subcats=(WD_SUBCATS as any)[sp.key] as {label:string;pctOfHarvest:number;fallKrDefault:number}[]|undefined;
      if(subcats&&subcats.length>0){
        for(const sc of subcats){
          if(EXCL_SUBCAT(sc.label))continue;
          const cnt=Math.round(totalHarvest*sc.pctOfHarvest);
          // Use 'Species — Subcat' format so planSpeciesFamilyKey maps it back to parent
          if(cnt>0)rows.push({gameType:`${sp.label} — ${sc.label}`,count:cnt,cost:sc.fallKrDefault,included:true});
        }
      }else{
        rows.push({gameType:sp.label,count:totalHarvest,cost:sp.fallKrDefault,included:true});
      }
    }
    setGenRows(rows);setShowGenPlan(true);
  };
  const doSaveGenPlan=async()=>{
    const season=new Date().getFullYear().toString();
    const toSave=genRows.filter(r=>r.included&&r.count>0);
    if(!toSave.length)return;
    setGenSaving(true);
    try{
      const saved=await Promise.all(toSave.map(r=>api.upsertMarkShootingPlan({markId:mark.id,season,gameType:r.gameType,plannedCount:r.count})));
      setPlan(prev=>{
        const next=[...prev];
        saved.forEach(s=>{const i=next.findIndex(p=>p.id===s.id);if(i>=0)next[i]={...next[i],...s};else next.push(s as any);});
        return next;
      });
      setShowGenPlan(false);
    }catch(e){console.error(e);}finally{setGenSaving(false);}
  };
  // Karta
  const[kartaLat,setKartaLat]=useState<string>(String(mark.mapLat??''));
  const[kartaLng,setKartaLng]=useState<string>(String(mark.mapLng??''));
  const[kartaSaving,setKartaSaving]=useState(false);
  const[kartaMapType,setKartaMapType]=useState<'mapnik'|'satellite'>('mapnik');
  // Ekonomi / Kostnadsmodell
  const[costModel,setCostModel]=useState<MarkCostModel|null>(null);
  const[costLd,setCostLd]=useState(false);
  const[costSaving,setCostSaving]=useState(false);
  const[costErr,setCostErr]=useState('');
  const[costForm,setCostForm]=useState({arsavgift:'',viltvardAvgiftPerAr:'',gastKostnadPerJakt:'',medlemKostnadPerJakt:'',boterOlovligtVilt:'',boterEftersok:''});
  const[fallavgifter,setFallavgifter]=useState<FallAvgift[]>([]);
  const[kottkostnad,setKottkostnad]=useState<KottKostnad[]>([]);
  const[newFall,setNewFall]=useState<FallAvgift>({viltslag:'Älg',kon:'Tjur',typ:'Helskovel',kostnad:0});
  const[newKott,setNewKott]=useState<KottKostnad>({viltslag:'Älg',kostnadPerKg:0});
  const[editMarkInfo,setEditMarkInfo]=useState(false);
  const[markInfoForm,setMarkInfoForm]=useState({areaHa:String(mark.areaHa||''),municipality:mark.municipality||'',county:mark.county||'',address:mark.address||''});
  const[markInfoSaving,setMarkInfoSaving]=useState(false);
  // Swish & ägarinfo
  const[swishForm,setSwishForm]=useState({swishNumber:mark.swishNumber||'',swishAmount:String(mark.swishAmount||0),ownerType:(mark.ownerType||'person') as 'person'|'foretag',ownerNumber:mark.ownerNumber||'',invoiceEmail:(mark as any).invoiceEmail||''});
  const[swishSaving,setSwishSaving]=useState(false);
  const[swishErr,setSwishErr]=useState('');
  // Annons-editor state
  const[annonsPublished,setAnnonsPublished]=useState<boolean>(!!mark.isPublished);
  // Jaktlag-prissättning (i annons-fliken)
  const[jklArsavgift,setJklArsavgift]=useState<string>(String(mark.costModel?.arsavgift&&mark.costModel.arsavgift>0?mark.costModel.arsavgift:''));
  const[jklKottIngar,setJklKottIngar]=useState<boolean>(!!mark.costModel?.kottIngar);
  const[jklAntalDrev,setJklAntalDrev]=useState<string>(String(mark.costModel?.antalDrevjakter&&mark.costModel.antalDrevjakter>0?mark.costModel.antalDrevjakter:''));
  const[jklKanBokaKal,setJklKanBokaKal]=useState<boolean>(!!mark.costModel?.kanBokaKalendar);
  const[jklFall,setJklFall]=useState<FallAvgift[]>(mark.costModel?.fallavgifter??[]);
  const[jklKott,setJklKott]=useState<KottKostnad[]>(mark.costModel?.kottkostnad??[]);
  const[jklNewFall,setJklNewFall]=useState<{viltslag:string;kon:string;kostnad:string}>({viltslag:'Älg',kon:'alla',kostnad:''});
  const[jklNewKott,setJklNewKott]=useState<{viltslag:string;kostnadPerKg:string}>({viltslag:'Älg',kostnadPerKg:''});
  const[jklSaving,setJklSaving]=useState(false);
  const[jklOk,setJklOk]=useState('');
  const[jklOpen,setJklOpen]=useState(false);
  const saveJklCost=async()=>{setJklSaving(true);setJklOk('');try{const cm=await api.upsertMarkCostModel(mark.id,{arsavgift:parseFloat(jklArsavgift)||0,viltvardAvgiftPerAr:costModel?.viltvardAvgiftPerAr??0,gastKostnadPerJakt:costModel?.gastKostnadPerJakt??0,medlemKostnadPerJakt:costModel?.medlemKostnadPerJakt??0,fallavgifter:jklFall,kottkostnad:jklKott,kottIngar:jklKottIngar,antalDrevjakter:parseInt(jklAntalDrev)||0,kanBokaKalendar:jklKanBokaKal,boterOlovligtVilt:costModel?.boterOlovligtVilt??0,boterEftersok:costModel?.boterEftersok??0});setCostModel(cm);setJklOk('Sparat!');setTimeout(()=>setJklOk(''),2500);}catch(e:any){alert(e.message||'Fel')}finally{setJklSaving(false);}};
  const VILTSLAG=['Älg','Kronhjort','Dovhjort','Rådjur','Vildsvin','Björn','Lo','Räv','Fasan','Hare','And','Kanadagås'];
  const KON_OPTS=['alla','Tjur/Bock','Ko/Get','Kalv/Kid'];
  const[annonsForm,setAnnonsForm]=useState({name:mark.name,description:mark.description||'',areaHa:String(mark.areaHa||''),county:mark.county||'',municipality:mark.municipality||'',address:mark.address||'',contactEmail:mark.contactEmail||'',contactPhone:mark.contactPhone||'',contactName:mark.contactName||'',pricePerYear:String(mark.pricePerYear||''),gameTypesJson:mark.gameTypesJson||'[]',difficulty:mark.difficulty||'',highlightsJson:mark.highlightsJson||'[]',includesJson:mark.includesJson||'[]',annualDrivenHunts:String(mark.annualDrivenHunts||''),afterhuntHuntersCount:String(mark.afterhuntHuntersCount||''),videoUrl:mark.videoUrl||'',fastighetsBeteckning:(mark as any).fastighetsBeteckning||''});
  const[annonsImageUrl,setAnnonsImageUrl]=useState(mark.imageUrl||'');
  const[annonsGallery,setAnnonsGallery]=useState<string[]>(mark.galleryUrls||[]);
  const[annonsSpecOcc,setAnnonsSpecOcc]=useState<Record<string,number>>(()=>{try{return JSON.parse(mark.speciesOccurrenceJson||'{}')}catch{return {}}});
  const[annonsFacilities,setAnnonsFacilities]=useState<Record<string,number>>(()=>{try{return JSON.parse(mark.facilitiesJson||'{}')}catch{return {}}});
  // Fetch fresh mark data on mount so stale parent prop never overwrites recently saved data
  useEffect(()=>{
    api.markById(markProp.id).then(m=>{
      if(!m)return;
      setMark(m);
      try{setAnnonsSpecOcc(JSON.parse(m.speciesOccurrenceJson||'{}'));}catch{}
      try{
        const raw=JSON.parse(m.speciesOccurrenceJson||'{}') as Record<string,number>;
        const norm:Record<string,number>={};
        for(const[k,v] of Object.entries(raw)) norm[planSpeciesFamilyKey(k)]=v;
        setPopLevels(norm);
      }catch{}
    }).catch(()=>{});
  },[markProp.id]); // eslint-disable-line
  const[annonsSaving,setAnnonsSaving]=useState(false);
  const[annonsErr,setAnnonsErr]=useState('');
  const[annonsOk,setAnnonsOk]=useState('');
  const[annonsUploading,setAnnonsUploading]=useState(false);
  const annonsImgRef=useRef<HTMLInputElement>(null);
  const annonsGallRef=useRef<HTMLInputElement>(null);
  const annonsVideoRef=useRef<HTMLInputElement>(null);
  const handleAnnonsMainImg=async(e:React.ChangeEvent<HTMLInputElement>)=>{const f=e.target.files?.[0];if(!f)return;setAnnonsUploading(true);try{const r=await api.uploadFile(f);setAnnonsImageUrl(r.url)}catch{setAnnonsErr('Bilduppladdning misslyckades')}finally{setAnnonsUploading(false);e.target.value=''}};
  const handleAnnonsGallery=async(e:React.ChangeEvent<HTMLInputElement>)=>{const fs=e.target.files;if(!fs?.length)return;setAnnonsUploading(true);try{const r=await api.uploadFiles(Array.from(fs));setAnnonsGallery(prev=>[...prev,...r.files.map((f:any)=>f.url)])}catch{setAnnonsErr('Bilduppladdning misslyckades')}finally{setAnnonsUploading(false);e.target.value=''}};
  const handleAnnonsVideo=async(e:React.ChangeEvent<HTMLInputElement>)=>{const f=e.target.files?.[0];if(!f)return;setAnnonsUploading(true);try{const r=await api.uploadFile(f);setAnnonsForm(p=>({...p,videoUrl:r.url}))}catch{setAnnonsErr('Videouppladdning misslyckades')}finally{setAnnonsUploading(false);e.target.value=''}};
  const saveAnnons=async()=>{setAnnonsSaving(true);setAnnonsErr('');setAnnonsOk('');try{const input:any={name:annonsForm.name,description:annonsForm.description||undefined,areaHa:annonsForm.areaHa?parseFloat(annonsForm.areaHa):undefined,county:annonsForm.county||undefined,municipality:annonsForm.municipality||undefined,address:annonsForm.address||undefined,contactEmail:annonsForm.contactEmail||undefined,contactPhone:annonsForm.contactPhone||undefined,contactName:annonsForm.contactName||undefined,imageUrl:annonsImageUrl||undefined,galleryUrls:annonsGallery.length?annonsGallery:undefined,videoUrl:annonsForm.videoUrl||undefined,gameTypesJson:annonsForm.gameTypesJson||undefined,difficulty:annonsForm.difficulty||undefined,highlightsJson:annonsForm.highlightsJson||undefined,includesJson:annonsForm.includesJson||undefined,speciesOccurrenceJson:Object.keys(annonsSpecOcc).length?JSON.stringify(annonsSpecOcc):undefined,facilitiesJson:Object.keys(annonsFacilities).length?JSON.stringify(annonsFacilities):undefined,annualDrivenHunts:annonsForm.annualDrivenHunts?parseInt(annonsForm.annualDrivenHunts):undefined,afterhuntHuntersCount:annonsForm.afterhuntHuntersCount?parseInt(annonsForm.afterhuntHuntersCount):undefined};const updated=await api.updateMark(mark.id,input);setMark(updated);setAnnonsOk('Markens uppgifter sparades');setTimeout(()=>setAnnonsOk(''),3000)}catch(ex:unknown){setAnnonsErr(ex instanceof Error?ex.message:'Något gick fel')}finally{setAnnonsSaving(false)}};
  const toggleAnnonsPublished=async()=>{const nv=!annonsPublished;try{await api.setMarkPublished(mark.id,nv);setAnnonsPublished(nv)}catch{setAnnonsErr('Kunde inte ändra publiceringsstatus')}};

  useEffect(()=>{api.myMarkRole(mark.id).then(p=>setMyPerms(p)).catch(()=>{})},[mark.id]);

  useEffect(()=>{
    if(tab==='annonser'&&!groupsLoaded){api.markGroups(mark.id).then(d=>{setGroups(d);setGroupsLoaded(true);}).catch(()=>{});}
    if(tab==='medlemmar'){setMembersLd(true);api.markMembers(mark.id).then(d=>{setMembers(d);setMembersLd(false)}).catch(()=>setMembersLd(false));api.markInviteLinks(mark.id).then(d=>setInvLinks(d)).catch(()=>{});if(!groupsLoaded){api.markGroups(mark.id).then(async d=>{setGroups(d);setGroupsLoaded(true);if(d.length===0&&(isAdmin||isMarkOwner)){const DEFAULT_GROUPS=[{name:'Jaktlag',color:'#EE8F09'},{name:'Hundförare',color:'#06B6D4'},{name:'Eftersöksjägare',color:'#8B5CF6'},{name:'Gästjägare',color:'#989a98'}];const created:typeof d=[];for(const dg of DEFAULT_GROUPS){try{const g=await api.createMarkGroup(mark.id,dg.name,dg.color);created.push(g);}catch{}}if(created.length>0)setGroups(created);}}).catch(()=>{})}}
    if(tab==='kalender'){setEventsLd(true);api.markCalendarEvents(mark.id,true).then(d=>{setEvents(d);setEventsLd(false)}).catch(()=>setEventsLd(false));if(members.length===0)api.markMembers(mark.id).then(d=>setMembers(d)).catch(()=>{});setTimeout(()=>{calendarRef.current?.scrollIntoView({behavior:'smooth',block:'start'})},100);}
    if(tab==='gruppchat'||tab==='karta'){
      let pollId: ReturnType<typeof setInterval>|null=null;
      const poll=(mid:string)=>{
        pollId=setInterval(()=>{
          api.markChatMessages(mid).then(d=>{
            if(!Array.isArray(d))return;
            setMessages(prev=>{
              const safe=Array.isArray(prev)?prev:[];
              const ids=new Set(safe.map((m:any)=>m.id));
              const newOnes=d.filter((m:any)=>m&&m.id&&!ids.has(m.id));
              return newOnes.length>0?[...safe,...newOnes]:safe;
            });
          }).catch(()=>{});
        },2500);
      };
      if(tab==='gruppchat'){
        setMessagesLd(true);
        api.markChatMessages(mark.id).then(d=>{setMessages(Array.isArray(d)?d:[]);setMessagesLd(false)}).catch(()=>setMessagesLd(false));
        poll(mark.id);
        return()=>{if(pollId)clearInterval(pollId);};
      }
      if(tab==='karta'){
        // Albumraden under kartan
        setAlbumLd(true);api.markAlbumImages(mark.id).then(d=>{setAlbumImages(d);setAlbumLd(false)}).catch(()=>setAlbumLd(false));
        api.markChatMessages(mark.id).then(d=>{if(Array.isArray(d))setMessages(d)}).catch(()=>{});
        poll(mark.id);
        return()=>{if(pollId)clearInterval(pollId);};
      }
    }
    if(tab==='avskjutningsplan'){setPlanLd(true);api.markShootingPlan(mark.id).then(d=>{setPlan(d);if(d.length>0){const normS=(s:string)=>{if(s.includes('/'))return s;const yr=Number(s);return isNaN(yr)?s:`${yr-1}/${yr}`;};const seasons=[...new Set(d.map((r:any)=>r.season))].sort() as string[];const cm=new Date().getMonth();const cy=new Date().getFullYear();const want=cm>=6?`${cy}/${cy+1}`:`${cy-1}/${cy}`;const best=seasons.find(s=>s===want||normS(s)===want)||seasons[seasons.length-1];if(best)setPlanSeason(best);}setPlanLd(false)}).catch(()=>setPlanLd(false))}
    if(tab==='kottdelning'){
      setMeatLd(true);
      Promise.all([
        api.markMeatSharing(mark.id).catch(()=>[] as MarkMeatSharingItem[]),
        api.markViltRegistrations(mark.id).catch(()=>[] as MarkViltRegistration[]),
        api.markFalltVilt(mark.id).catch(()=>[] as any[]),
        api.markMeatQueue(mark.id).catch(()=>[] as MarkMeatQueueItem[]),
      ]).then(([meat,regs,pins,q])=>{setMeatItems(meat);setViltRegs(regs);setFalltViltPins(pins);setQueue(q);setMeatLd(false);}).catch(()=>setMeatLd(false));
      if(members.length===0){api.markMembers(mark.id).then(d=>setMembers(d)).catch(()=>{});}
      api.markCostModel(mark.id).then(d=>{if(d){setKottkostnad(d.kottkostnad||[]);setFallavgifter(d.fallavgifter||[]);} }).catch(()=>{});
    }
    if(tab==='album'){setAlbumLd(true);api.markAlbumImages(mark.id).then(d=>{setAlbumImages(d);setAlbumLd(false)}).catch(()=>setAlbumLd(false))}
    if(tab==='ekonomi'){setCostLd(true);api.markCostModel(mark.id).then(d=>{if(d){setCostModel(d);setCostForm({arsavgift:String(d.arsavgift||0),viltvardAvgiftPerAr:String(d.viltvardAvgiftPerAr||0),gastKostnadPerJakt:String(d.gastKostnadPerJakt||0),medlemKostnadPerJakt:String(d.medlemKostnadPerJakt||0),boterOlovligtVilt:String(d.boterOlovligtVilt||0),boterEftersok:String(d.boterEftersok||0)});setFallavgifter(d.fallavgifter||[]);setKottkostnad(d.kottkostnad||[]);}setCostLd(false);}).catch(()=>setCostLd(false));
      // Load swish+owner settings
      const headers:Record<string,string>={'Content-Type':'application/json','X-Api-Key':'dev-secret'};
      const t=localStorage.getItem('qvenari_access_token');if(t)Object.assign(headers,bearerHeaders(t));
      fetch(resolveUrl('/graphql'),{method:'POST',headers,body:JSON.stringify({query:`query SW($id:ID!){mark(id:$id){swishNumber swishAmount ownerType ownerNumber invoiceEmail}}`,variables:{id:mark.id}})}).then(r=>r.json()).then(d=>{if(d.data?.mark){const m=d.data.mark;setSwishForm({swishNumber:m.swishNumber||'',swishAmount:String(m.swishAmount||0),ownerType:(m.ownerType||'person') as 'person'|'foretag',ownerNumber:m.ownerNumber||'',invoiceEmail:m.invoiceEmail||''});}}).catch(()=>{});}
  },[tab,mark.id]);

  // Scroll chat container (not window) to bottom when messages update — prevents focus loss on iOS
  useEffect(()=>{if(tab==='gruppchat'&&chatContainerRef.current){const el=chatContainerRef.current;el.scrollTop=el.scrollHeight;}},[messages]);

  useEffect(()=>{
    if(albumViewer===null)return;
    const handleKey=(e:KeyboardEvent)=>{
      if(e.key==='ArrowLeft'&&albumViewer>0)setAlbumViewer(albumViewer-1);
      if(e.key==='ArrowRight'&&albumViewer<albumImages.length-1)setAlbumViewer(albumViewer+1);
      if(e.key==='Escape')setAlbumViewer(null);
    };
    window.addEventListener('keydown',handleKey);
    return()=>window.removeEventListener('keydown',handleKey);
  },[albumViewer,albumImages.length]);

  const doInvite=()=>{if(!invEmail||!invName)return;setInvBusy(true);api.addMarkMember(mark.id,{userEmail:invEmail,name:invName,role:invRole}).then(m=>{setMembers(prev=>[...prev,m]);setInvEmail('');setInvName('');setInvBusy(false)}).catch(()=>setInvBusy(false))};
  const doCreateEvent=()=>{if(!calTitle||!calStart)return;setCalBusy(true);
    // Auto-add creator as member invite if not already in list
    const finalInvites=[...calInvites];
    if(user&&!finalInvites.some(i=>i.email===user.email)){
      finalInvites.unshift({email:user.email,name:user.name||user.email,inviteType:'member'});
    }
    api.createMarkCalendarEvent(mark.id,{title:calTitle,eventType:calType,startTime:calStart,endTime:calEnd||undefined,location:calLoc||undefined,invites:finalInvites.length>0?finalInvites:undefined,createQmeetMeeting:calQmeet,satPlanJson:calSatPlan?JSON.stringify(calSatPlan):undefined}).then(ev=>{
      setEvents(prev=>[...prev,ev].sort((a,b)=>new Date(a.startTime).getTime()-new Date(b.startTime).getTime()));
      // Notify parent to update dashboard calendar
      if(onCalEventCreated)onCalEventCreated(ev);
      setCalTitle('');setCalStart('');setCalEnd('');setCalLoc('');setCalInvites([]);setCalSatPlan(undefined);setCalBusy(false)
    }).catch(()=>setCalBusy(false))};
  const CHAT_KILL_PAT=/\b(sköt|skjutit|skjuten|sjkuten|skjutet|skjuttes|fällt|fälld|fällde|träffad|träffade|nedlagd|nedlagt|knäckt|prick|skutt|lagt ned|sköts|ner|nere|avlivad|klar|klart|tagen|taget|ihjäl)\b/i;
  const CHAT_GAME_ANIMALS=[
    {sv:'Älg',fk:'alg',kw:['älg','älgko','älgtjur','älgkalv']},
    {sv:'Dovhjort',fk:'dovhjort',kw:['dovhjort','dovko','dovtjur','dovkalv','dovhind']},
    {sv:'Kronhjort',fk:'kronhjort',kw:['kronhjort','kapitalhjort']},
    {sv:'Rådjur',fk:'radjur',kw:['rådjur','råbock','råget','råkid']},
    {sv:'Vildsvin',fk:'vildsvin',kw:['vildsvin','galt','sugga','smågris','årsgris']},
    {sv:'Mufflon',fk:'mufflon',kw:['mufflon']},
    {sv:'Björn',fk:'bjorn',kw:['björn']},
    {sv:'Hare',fk:'hare',kw:['hare']},
    {sv:'Fasan',fk:'fasan',kw:['fasan']},
    {sv:'Orre',fk:'orre',kw:['orre']},
    {sv:'Tjäder',fk:'tjader',kw:['tjäder']},
    {sv:'Andfågel',fk:'andfagel',kw:['and','gräsand','kricka','andfågel']},
    {sv:'Kanadagås',fk:'kanadagas',kw:['kanadagås','grågås','gäss']},
    {sv:'Räv',fk:'rav',kw:['räv']},
    {sv:'Grävling',fk:'gravling',kw:['grävling']},
  ];
  const doSendChat=()=>{
    if(!chatMsg.trim())return;
    const msgText=chatMsg.trim();
    setChatBusy(true);
    api.sendMarkChatMessage(mark.id,msgText).then(raw=>{
      const msgs=Array.isArray(raw)?raw:raw&&typeof raw==='object'&&'id'in raw?[raw as MarkChatMessage]:[];
      setMessages(prev=>[...(Array.isArray(prev)?prev:[]),...msgs]);
      setChatMsg('');
      setChatBusy(false);
      // Kill detection — trigger on explicit kill word OR short message (≤40 chars) likely to be a hunt report
      const ll=msgText.toLowerCase();
      const hasAnimal=CHAT_GAME_ANIMALS.some(g=>g.kw.some(k=>ll.includes(k)));
      const isKillMsg=CHAT_KILL_PAT.test(msgText)||(hasAnimal&&msgText.trim().length<=40);
      if(isKillMsg){
        for(const g of CHAT_GAME_ANIMALS){
          if(g.kw.some(k=>ll.includes(k))){
            const subcatLabels=WD_SM[g.fk];
            const fullSubcats=(subcatLabels&&subcatLabels.length>0)?subcatLabels.map(s=>`${g.sv} — ${s}`):null;
            if(fullSubcats){
              // Show inline question in chat
              const botMsg:MarkChatMessage={id:`bot_kill_${Date.now()}`,markId:mark.id,senderEmail:'system@qvenari.com',senderName:'Qvenari',message:JSON.stringify({family:g.sv,familyKey:g.fk,subcats:fullSubcats}),messageType:'kill_question',imageUrl:null as any,fileUrl:null as any,fileName:null as any,fileType:null as any,createdAt:new Date().toISOString()};
              setMessages(prev=>[...(Array.isArray(prev)?prev:[]),botMsg]);
            } else {
              // No subcats — update plan directly and create viltReg
              const fk=g.fk;
              const planRow=plan.find(p=>p.gameType===g.sv)||plan.find(p=>planSpeciesFamilyKey(p.gameType)===fk);
              if(planRow){
                const newActual=(planRow.actualCount||0)+1;
                api.upsertMarkShootingPlan({markId:mark.id,season:planRow.season,gameType:planRow.gameType,plannedCount:planRow.plannedCount,actualCount:newActual,costPerAnimal:planRow.costPerAnimal,notes:`Uppdaterad från chatt ${new Date().toLocaleDateString('sv-SE')}`}).then(()=>{api.markShootingPlan(mark.id).then(d=>setPlan(d)).catch(()=>{});}).catch(()=>{});
              }
              api.addViltRegistration({markId:mark.id,season:planSeason,gameType:g.sv,shotDate:new Date().toISOString().slice(0,10)}).then(()=>{api.markViltRegistrations(mark.id).then(d=>setViltRegs(d)).catch(()=>{});}).catch(()=>{});
            }
            break;
          }
        }
      }
    }).catch(()=>setChatBusy(false));
  };
  const [chatAttachMenu,setChatAttachMenu]=useState(false);
  // Chat sync helpers
  const chatSyncH={...bearerHeaders(localStorage.getItem('qvenari_access_token')||''),'X-Api-Key':'dev-secret','Content-Type':'application/json'};
  const loadChatInts=async()=>{try{const r=await apiFetch(`/api/integrations/${mark.id}`,{headers:chatSyncH});if(r.ok){const d=await r.json();setChatInts(d);const tg=d.find((i:any)=>i.platform==='telegram');if(tg){try{const creds=JSON.parse(tg.credentials_json||'{}');setChatTgToken(creds.botToken||'');}catch{}}/* chat_id auto-detected — never pre-fill */}}catch{}};
  useEffect(()=>{if(tab==='gruppchat'){loadChatInts();}return()=>{if(chatWaPollRef.current){clearInterval(chatWaPollRef.current);chatWaPollRef.current=null;}};},[tab]);
  // Auto-poll WA when sync panel open
  useEffect(()=>{if(chatSyncOpen==='whatsapp'){const wa=chatInts.find((i:any)=>i.platform==='whatsapp');if(wa&&!wa.connected&&!chatWaPollRef.current){chatWaPollRef.current=window.setInterval(async()=>{try{const r=await apiFetch(`/api/integrations/${mark.id}`,{headers:chatSyncH});if(r.ok){const ints=await r.json();setChatInts(ints);const w=ints.find((i:any)=>i.platform==='whatsapp');if(w?.connected){clearInterval(chatWaPollRef.current!);chatWaPollRef.current=null;setChatWaQR(null);return;}}const qr=await apiFetch(`/api/integrations/${mark.id}/whatsapp/qr`,{headers:chatSyncH});if(qr.ok){const d=await qr.json();if(d.qr)setChatWaQR(d.qr);}}catch{}},3000);}if(wa?.connected&&chatWaPollRef.current){clearInterval(chatWaPollRef.current);chatWaPollRef.current=null;}}else{if(chatWaPollRef.current){clearInterval(chatWaPollRef.current);chatWaPollRef.current=null;}};},[chatSyncOpen,chatInts]);
  // Fetch bot info when Telegram panel is open and a bot token exists
  useEffect(()=>{
    if(chatSyncOpen==='telegram'){
      const tg=chatInts.find((i:any)=>i.platform==='telegram');
      if(tg){
        try{const creds=JSON.parse(tg.credentials_json||'{}');if(creds.botToken){
          apiFetch(`/api/integrations/${mark.id}/telegram/bot-info`,{headers:chatSyncH})
            .then(r=>r.ok?r.json():null)
            .then(d=>{if(d)setTgBotInfo(d);})
            .catch(()=>{});
        }}catch{}
      }
    } else {
      setTgBotInfo(null);
    }
  },[chatSyncOpen,chatInts]);
  const startChatWa=async()=>{await apiFetch(`/api/integrations/${mark.id}/whatsapp/start`,{method:'POST',headers:chatSyncH,body:JSON.stringify({groupId:mark.name})});await loadChatInts();const qr=await apiFetch(`/api/integrations/${mark.id}/whatsapp/qr`,{headers:chatSyncH});if(qr.ok){const d=await qr.json();if(d.qr)setChatWaQR(d.qr);}};
  const removeChatWa=async()=>{await apiFetch(`/api/integrations/${mark.id}/whatsapp`,{method:'DELETE',headers:chatSyncH});setChatWaQR(null);await loadChatInts();};
  const saveChatTg=async()=>{setChatTgBusy(true);try{await apiFetch(`/api/integrations/${mark.id}/telegram`,{method:'POST',headers:chatSyncH,body:JSON.stringify({botToken:chatTgToken,active:true})});await loadChatInts();}finally{setChatTgBusy(false);}};
  const removeChatTg=async()=>{try{await apiFetch(`/api/integrations/${mark.id}/telegram`,{method:'DELETE',headers:chatSyncH});await loadChatInts();}catch(e){console.error('removeChatTg failed',e);}};
  const saveChatSms=async()=>{setSmsBusy(true);setSmsResult(null);try{const r=await apiFetch(`/api/integrations/${mark.id}/sms`,{method:'POST',headers:chatSyncH,body:JSON.stringify({})});const d=await r.json();setSmsResult({message:d.message??'Sparad',success:d.success??r.ok});await loadChatInts();}catch(e:any){setSmsResult({message:'Fel: '+e.message,success:false});}finally{setSmsBusy(false);}};
  const removeChatSms=async()=>{try{await apiFetch(`/api/integrations/${mark.id}/sms`,{method:'DELETE',headers:chatSyncH});setSmsResult(null);await loadChatInts();}catch(e){console.error('removeChatSms failed',e);}};
  const syncSmsMembrs=async()=>{setSmsBusy(true);try{const r=await apiFetch(`/api/integrations/${mark.id}/sms/sync-members`,{method:'POST',headers:chatSyncH});const d=await r.json();setSmsResult({message:`Synkat ${d.membersSync??0} medlemmar — status: ${d.status}`,success:d.success??r.ok});}catch(e:any){setSmsResult({message:'Fel: '+e.message,success:false});}finally{setSmsBusy(false);}};
  const doUploadFile=async(accept:string, msgType:string, useCamera=false)=>{
    setChatAttachMenu(false);
    const inp=document.createElement('input');inp.type='file';inp.accept=accept;inp.multiple=!useCamera;
    if(useCamera)inp.setAttribute('capture','environment');
    inp.style.display='none';
    document.body.appendChild(inp);
    const cleanup=()=>{ try{if(document.body.contains(inp))document.body.removeChild(inp);}catch(_){} };
    inp.addEventListener('change',async()=>{
      if(!inp.files?.length){cleanup();return;}
      setChatBusy(true);
      const files=Array.from(inp.files);
      cleanup(); // safe to remove now that files are captured
      for(const file of files){
        try{
          const fd=new FormData();fd.append('file',file);
          const res=await apiFetch('/api/upload',{method:'POST',body:fd});
          if(!res.ok){const err=await res.text();throw new Error(`Upload failed: ${err}`);}
          const data=await res.json();
          if(data.type==='image'){
            const m=await api.sendMarkChatMessage(mark.id,undefined,data.url,msgType||'image');
            const arr=Array.isArray(m)?m:m&&typeof m==='object'&&'id'in m?[m as MarkChatMessage]:[];
            setMessages(prev=>[...(Array.isArray(prev)?prev:[]),...arr]);
          }else if(data.type==='video'){
            const m=await api.sendMarkChatMessage(mark.id,undefined,undefined,'video',data.url,data.originalName,'video');
            const arrV=Array.isArray(m)?m:m&&typeof m==='object'&&'id'in m?[m as MarkChatMessage]:[];
            setMessages(prev=>[...(Array.isArray(prev)?prev:[]),...arrV]);
          }else{
            const m=await api.sendMarkChatMessage(mark.id,undefined,undefined,'file',data.url,data.originalName,data.type);
            const arrF=Array.isArray(m)?m:m&&typeof m==='object'&&'id'in m?[m as MarkChatMessage]:[];
            setMessages(prev=>[...(Array.isArray(prev)?prev:[]),...arrF]);
          }
        }catch(e){console.error('Upload error:',e);}
      }
      setChatBusy(false);
    });
    inp.addEventListener('cancel',cleanup);
    inp.click();
  };
  const doSavePlan=(item:MarkShootingPlanItem|null,gt:string)=>{const season=item?.season??planSeason;api.upsertMarkShootingPlan({markId:mark.id,season,gameType:gt,plannedCount:editPlanned}).then(u=>{setPlan(prev=>{const exists=prev.some(p=>p.id===u.id);return exists?prev.map(p=>p.id===u.id?{...p,plannedCount:u.plannedCount,costPerAnimal:u.costPerAnimal,gameType:u.gameType}:p):[...prev,u];});setEditPlanId(null)}).catch(()=>{})}

  const avatarColor=(email?:string|null)=>{const s=email||'?';let h=0;for(let i=0;i<s.length;i++)h=s.charCodeAt(i)+((h<<5)-h);return`hsl(${h%360},50%,45%)`};
  // Re-compute style constants using current T (module-level constants capture dark theme at load time)
  const cS:React.CSSProperties={background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:20};
  const iS:React.CSSProperties={width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:4,padding:'10px 14px',color:T.text,fontSize:14,outline:'none',boxSizing:'border-box',fontFamily:'inherit'};
  const lS:React.CSSProperties={display:'block',color:T.textSub,fontSize:11,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:6};
  const bP:React.CSSProperties={background:T.amber,color:'#000',border:'none',borderRadius:5,padding:'12px 24px',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'inherit'};
  const secT:React.CSSProperties={color:T.textMuted,fontSize:11,fontWeight:600,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:16};
  const ROLE_OPTIONS=[
    {value:'jaktledare',label:'Jaktledare',color:T.amber},
    {value:'vice_jaktledare',label:'Vice jaktledare',color:T.amberLight},
    {value:'admin',label:'Admin',color:'#e74c3c'},
    {value:'hundforare',label:'Hundförare',color:T.accent2},
    {value:'jagare',label:'Jägare',color:T.accent3},
    {value:'eftersoksjagare',label:'Eftersöksjägare',color:T.accent1},
    {value:'medhjalprare',label:'Medhjälpare',color:'#9b59b6'},
    {value:'gast',label:'Gäst',color:T.textSub},
  ];
  const roleColor=(r:string)=>{const found=ROLE_OPTIONS.find(o=>o.value===r.toLowerCase()||o.label.toLowerCase()===r.toLowerCase());return found?.color||T.accent3};
  const roleLabel=(r:string)=>{const found=ROLE_OPTIONS.find(o=>o.value===r.toLowerCase()||o.label.toLowerCase()===r.toLowerCase());return found?.label||r};

  const sortedEvents=[...events].sort((a,b)=>new Date(a.startTime).getTime()-new Date(b.startTime).getTime());
  const gameTypesInQueue=[...new Set(queue.map(q=>q.gameType))];

  const latestChatHuntMeetingId=useMemo(()=>{
    const arr=Array.isArray(messages)?messages:[];
    for(let i=arr.length-1;i>=0;i--){
      const m=arr[i];
      if(m.messageType==='hunt_started'&&m.fileUrl)return m.fileUrl;
    }
    return null;
  },[messages]);

  /**
   * Pågående jakt på marken enligt servern (mark_hunts) — oavsett om den startades från telefonen
   * eller klockan. Qmeet frågas inte: det tappar möten, och klockans jakt har inget LiveKit-rum.
   * Ett nytt hunt_started i gruppchatten hämtar direkt, annars var 15:e sekund.
   */
  const[joinableHuntMeetingIdVerified,setJoinableHuntMeetingIdVerified]=useState<string|null>(null);
  useEffect(()=>{
    if(tab!=='karta'&&tab!=='gruppchat')return;
    let cancelled=false;
    const load=()=>{api.activeMarkHunt(mark.id).then(h=>{if(!cancelled)setJoinableHuntMeetingIdVerified(h?.meetingId??null);}).catch(()=>{});};
    load();
    const tid=setInterval(load,15000);
    return()=>{cancelled=true;clearInterval(tid);};
  },[mark.id,tab,latestChatHuntMeetingId]);

  /** A3: jaktens tid och markens gräns sparas på telefonen, så att bilder från jakten kan hämtas (huntMediaSync.ts). */
  const huntMediaMidRef=React.useRef<string|null>(null);
  useEffect(()=>{
    const prev=huntMediaMidRef.current;const mid=huntSession?.meetingId??null;
    huntMediaMidRef.current=mid;
    if(prev&&prev!==mid)finishHuntWindow(prev);
    if(!mid||mid===prev)return;
    const base={key:mid,markId:mark.id,markName:mark.name,eventId:huntSession?.calEventId??null,to:null,coordinatesJson:mark.coordinatesJson,mapLat:mark.mapLat,mapLng:mark.mapLng};
    api.activeMarkHunt(mark.id)
      .then(h=>rememberHuntWindow({...base,from:h?.meetingId===mid?h.startedAt:Date.now()}))
      .catch(()=>rememberHuntWindow({...base,from:Date.now()}));
  },[huntSession?.meetingId]); // eslint-disable-line

  /** Auto-close hunt after 12 h — prevents zombie sessions. */
  useEffect(()=>{
    if(!huntSession?.meetingId)return;
    const t=window.setTimeout(()=>{
      endHuntMeeting(huntSession.meetingId);
      setHuntSession(null);
    },12*60*60*1000);
    return()=>clearTimeout(t);
  },[huntSession?.meetingId]);

  /** Stänger jakten här när den avslutats för alla (servern avgör, inte Qmeet). */
  useEffect(()=>{
    const mid=huntSession?.meetingId;
    if(!mid)return;
    let cancelled=false;
    const check=async()=>{
      try{
        const h=await api.activeMarkHunt(mark.id);
        if(cancelled)return;
        // En nyare jakt på marken betyder inte att den här är slut — bara att ingen pågår gör det.
        // Kartan (KartaTab) visas kanske inte just nu — spara jakten som träningspass här (hunterHealth.ts).
        if(!h){void finishHuntWorkout(mid);setHuntSession(null);}
      }catch{/* behåll session vid nätverksfel */}
    };
    void check();
    const tid=setInterval(check,15000);
    return()=>{cancelled=true;clearInterval(tid);};
  },[huntSession?.meetingId,mark.id]);

  function handleOpenPlanFromMap(){
    setCalType('hunt');
    setCalTitle(t=>t.trim()?t:`Jakt — ${mark.name}`);
    setShowSatPlanerare(true);
  }
  // UX4: jaktknappen på en annan marks karta valde den här marken – starta eller planera direkt när den öppnas.
  useEffect(()=>{
    const a=takeHuntAction(markProp.id);
    if(a==='plan'){setTab('karta');handleOpenPlanFromMap();}
    else if(a==='start'){setTab('karta');void handleSnabbstartMark();}
  },[markProp.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickHuntMark=(markId:string,action:'start'|'plan')=>{
    requestHuntAction(markId,action);
    window.dispatchEvent(new CustomEvent(OPEN_MARK_TAB_EVENT,{detail:{markId,tab:'karta'}}));
  };

  return(
    <div style={{minHeight:'100vh',background:T.bg}}>
      {/* Skal: brödsmulor, titelblock och flikrad (DR1 omgång 2). Flikar efter roll, resten under Mer. */}
      {(()=>{
        const permittedTabs=MD_TABS.filter(({k})=>{
          if(isDemo&&!['karta','kalender','gruppchat','album','medlemmar'].includes(k))return false; // AS1a: bara det som går att titta på
          if(k==='avskjutningsplan'&&!(myPerms?.canManageShootingPlan||myPerms?.canRecordKill||isAdmin))return false;
          if(k==='kottdelning'&&!(myPerms?.canManageMeatSharing||myPerms?.canRecordKill||isAdmin))return false;
          if(k==='ansokningar'&&!(isAdmin||myPerms?.canManageMembers||myPerms?.canEditMark))return false; // D5: antagning och äldre ansökningar
          return true;
        });
        const hasAnnons=!!(isAdmin||myPerms?.canEditMark);
        const items:TabItem<MdTab>[]=[
          ...permittedTabs.map(({k,l})=>({key:k,label:l})),
          ...(hasAnnons?[{key:'annonser' as MdTab,label:'Annonser'},{key:'annons' as MdTab,label:'Inställningar'}]:[]),
        ];
        const {ordered,maxVisible}=orderMarkTabs(items,hasAnnons,mob);
        return(
          <MarkHeader mark={mark} mob={mob} tabs={ordered} tab={tab} onTab={setTab}
            maxVisible={maxVisible}
            roleLabel={isMarkOwner?'Markägare':myPerms?.label}
            memberCount={members.length||undefined}
            onBack={onBack}
            onSettings={hasAnnons?()=>setTab('annons'):undefined}
            onPublicPage={onOpenPublicPage}
            subBar={tab==='gruppchat'&&user?(
              // CH2: från en jaktmark hamnar man direkt i markens chatt; härifrån kommer man till alla chattar. UX7: fast under flikarna.
              <div style={{display:'flex',alignItems:'center',gap:12,minWidth:0}}>
                <button type="button" onClick={()=>window.dispatchEvent(new Event(OPEN_CHATS_EVENT))}
                  style={{display:'inline-flex',alignItems:'center',gap:6,minHeight:44,padding:'0 4px',background:'transparent',border:'none',color:T.amber,fontSize:15,fontWeight:600,cursor:'pointer',fontFamily:'inherit',flexShrink:0}}>
                  <span aria-hidden style={{fontSize:20,lineHeight:1}}>‹</span>Alla chattar
                </button>
                {!mob&&<span style={{fontSize:14,color:T.textMuted,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{mark.name}</span>}
              </div>
            ):undefined}
            onDelete={isAdmin?async()=>{if(!confirm(`Radera marken "${mark.name}" permanent? Detta går inte att ångra.`))return;try{const ok=await api.deleteMark(mark.id);if(!ok){alert('Radering misslyckades – marken hittades inte.');return;}}catch(e:any){alert('Kunde inte radera: '+(e.message||e));return;}onDelete?.(mark.id);onBack();}:undefined}
            notice={(isDemo||sbError)?<>{isDemo&&<DemoMarkBanner onHidden={()=>{onDelete?.(mark.id);onBack();}}/>}{sbError?(
              <div role="alert" style={{maxWidth:1264,margin:'12px auto 0',padding:'0 clamp(16px,3vw,32px)'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,background:T.dangerSoft,color:T.danger,borderRadius:12,padding:'0 0 0 14px',fontSize:14,fontWeight:600}}>
                  <span style={{flex:1}}>{sbError}</span>
                  <IconButton label="Stäng felmeddelandet" onClick={()=>setSbError('')} style={{color:T.danger}}><Icon as={X}/></IconButton>
                </div>
              </div>
            ):null}</>:null}
          />
        );
      })()}

      {/* Flikinnehållet: samma sidmarginal som titelblocket i MarkHeader. */}
      {/* UX9: i chatten ligger skrivfältet fast längst ner — ingen extra luft under, annars kan sidan skrollas förbi sista meddelandet. */}
      <div style={{maxWidth:1264,margin:'0 auto',padding:mob?`20px 16px ${tab==='gruppchat'?0:80}px`:`24px clamp(16px,3vw,32px) ${tab==='gruppchat'?0:80}px`}}>

        {/* ── MEDLEMMAR ── */}
        {tab==='medlemmar'&&<MedlemmarTab
          mark={mark} user={user} isAdmin={isAdmin} isMarkOwner={isMarkOwner} myPerms={myPerms}
          onOpenEkonomi={g=>openEkonomi(g)} onOpenAds={()=>setTab('annonser')}
          members={members} membersLd={membersLd} groups={groups} groupsLoaded={groupsLoaded}
          setGroups={setGroups} setGroupsLoaded={setGroupsLoaded} setMembers={setMembers}
          expandedGroup={expandedGroup} setExpandedGroup={setExpandedGroup}
          newGroupName={newGroupName} setNewGroupName={setNewGroupName}
          newGroupColor={newGroupColor} setNewGroupColor={setNewGroupColor}
          showNewGroupForm={showNewGroupForm} setShowNewGroupForm={setShowNewGroupForm}
          editingGroupId={editingGroupId} setEditingGroupId={setEditingGroupId}
          editGroupName={editGroupName} setEditGroupName={setEditGroupName}
          editGroupColor={editGroupColor} setEditGroupColor={setEditGroupColor}
          editingGroupPermsId={editingGroupPermsId} setEditingGroupPermsId={setEditingGroupPermsId}
          editGroupPerms={editGroupPerms} setEditGroupPerms={setEditGroupPerms}
          editingJaktklubId={editingJaktklubId} setEditingJaktklubId={setEditingJaktklubId}
          editJaktklub={editJaktklub} setEditJaktklub={setEditJaktklub}
          jaktklubApps={jaktklubApps} setJaktklubApps={setJaktklubApps}
          jaktklubAppsLoaded={jaktklubAppsLoaded} setJaktklubAppsLoaded={setJaktklubAppsLoaded}
          events={events} setEvents={setEvents}
          invEmail={invEmail} setInvEmail={setInvEmail} invName={invName} setInvName={setInvName}
          invRole={invRole} setInvRole={setInvRole} invBusy={invBusy} setInvBusy={setInvBusy}
          invLinks={invLinks} setInvLinks={setInvLinks} invLinksLd={invLinksLd}
          ilLabel={ilLabel} setIlLabel={setIlLabel} ilRole={ilRole} setIlRole={setIlRole}
          ilMaxUses={ilMaxUses} setIlMaxUses={setIlMaxUses} ilExpires={ilExpires} setIlExpires={setIlExpires}
          ilBusy={ilBusy} setIlBusy={setIlBusy} ilCopied={ilCopied} setIlCopied={setIlCopied}
          ilShowForm={ilShowForm} setIlShowForm={setIlShowForm}
          onJaktklubSettingsChanged={onJaktklubSettingsChanged}
          T={T} cS={cS} iS={iS} lS={lS} bP={bP} secT={secT}
          ROLE_OPTIONS={ROLE_OPTIONS} avatarColor={avatarColor}
        />}
        {/* ── KALENDER ── */}
        {tab==='kalender'&&<KalenderTab
          mark={mark} user={user} isAdmin={isAdmin} myPerms={myPerms}
          eventsLd={eventsLd} sortedEvents={sortedEvents} setEvents={setEvents}
          setTab={setTab} setHuntSession={setHuntSession}
          setShowSatPlanerare={setShowSatPlanerare} setCalType={setCalType}
          completedHuntDetail={completedHuntDetail} setCompletedHuntDetail={setCompletedHuntDetail}
          setCalDetailEvent={setCalDetailEvent}
          guestAdEventId={guestAdEventId} setGuestAdEventId={setGuestAdEventId}
          guestAds={guestAds} setGuestAds={setGuestAds}
          prevGuests={prevGuests} setPrevGuests={setPrevGuests}
          pendingApplicants={pendingApplicants} setPendingApplicants={setPendingApplicants}
          guestAdBusy={guestAdBusy} setGuestAdBusy={setGuestAdBusy}
          editAdRole={editAdRole} setEditAdRole={setEditAdRole}
          adForm={adForm} setAdForm={setAdForm}
          calendarRef={calendarRef}
        />}

        {/* ── GRUPPCHAT ── */}
        {/* ── GRUPPCHAT ── */}
        {tab==='gruppchat'&&<GruppchatTab
          mark={mark} user={user} isAdmin={isAdmin} myPerms={myPerms}
          messagesLd={messagesLd} messages={messages} setMessages={setMessages}
          plan={plan} setPlan={setPlan} planSeason={planSeason} setViltRegs={setViltRegs}
          chatMsg={chatMsg} setChatMsg={setChatMsg} chatBusy={chatBusy} setChatBusy={setChatBusy}
          chatAttachMenu={chatAttachMenu} setChatAttachMenu={setChatAttachMenu}
          chatEndRef={chatEndRef} chatContainerRef={chatContainerRef}
          chatSyncOpen={chatSyncOpen} setChatSyncOpen={setChatSyncOpen}
          chatInts={chatInts} setChatInts={setChatInts}
          chatWaQR={chatWaQR} setChatWaQR={setChatWaQR}
          chatTgToken={chatTgToken} setChatTgToken={setChatTgToken}
          chatTgChatId={chatTgChatId} setChatTgChatId={setChatTgChatId}
          chatTgBusy={chatTgBusy} setChatTgBusy={setChatTgBusy}
          chatWaGroupBusy={chatWaGroupBusy} setChatWaGroupBusy={setChatWaGroupBusy}
          chatWaPhoneInput={chatWaPhoneInput} setChatWaPhoneInput={setChatWaPhoneInput}
          tgBotInfo={tgBotInfo} smsBusy={smsBusy} smsResult={smsResult} setSmsResult={setSmsResult}
          joinableHuntMeetingIdVerified={joinableHuntMeetingIdVerified}
          handleJoinFromChat={handleJoinFromChat}
          loadChatInts={loadChatInts} startChatWa={startChatWa} removeChatWa={removeChatWa}
          saveChatTg={saveChatTg} removeChatTg={removeChatTg}
          saveChatSms={saveChatSms} removeChatSms={removeChatSms} syncSmsMembrs={syncSmsMembrs}
          doUploadFile={doUploadFile} doSendChat={doSendChat}
          avatarColor={avatarColor} CHAT_GAME_ANIMALS={CHAT_GAME_ANIMALS}
        />}
        {/* ── AVSKJUTNINGSPLAN ── */}
        {/* ── AVSKJUTNINGSPLAN ── */}
        {tab==='avskjutningsplan'&&<AvskjutningsplanTab
          mark={mark} setMark={setMark}
          planLd={planLd} plan={plan} setPlan={setPlan}
          planSeason={planSeason} setPlanSeason={setPlanSeason}
          planEditMode={planEditMode} setPlanEditMode={setPlanEditMode}
          planDraft={planDraft} setPlanDraft={setPlanDraft}
          planSaving={planSaving} setPlanSaving={setPlanSaving}
          planCreating={planCreating} setPlanCreating={setPlanCreating}
          showGenPlan={showGenPlan} setShowGenPlan={setShowGenPlan}
          genRows={genRows} setGenRows={setGenRows}
          genSaving={genSaving} setGenSaving={setGenSaving}
          newSpeciesName={newSpeciesName} setNewSpeciesName={setNewSpeciesName}
          popLevels={popLevels} setPopLevels={setPopLevels}
          popCounts={popCounts} setPopCounts={setPopCounts}
          savePopLevels={savePopLevels} buildPopPlan={buildPopPlan}
          isAdmin={isAdmin} isMarkOwner={isMarkOwner} myPerms={myPerms}
          EXCL_SUBCAT={EXCL_SUBCAT}
          T={T} iS={iS} bP={bP} bSc={bSc} secT={secT}
        />}

        {/* ── VILTKÖTT ── */}
        {/* ── VILTKÖTT ── */}
        {tab==='kottdelning'&&<KottdelningTab
          mark={mark} meatLd={meatLd} members={members}
          viltRegs={viltRegs} setViltRegs={setViltRegs}
          falltViltPins={falltViltPins} setFalltViltPins={setFalltViltPins}
          meatItems={meatItems} setMeatItems={setMeatItems}
          queue={queue} plan={plan}
          fallavgifter={fallavgifter} kottkostnad={kottkostnad}
          isAdmin={isAdmin} myPerms={myPerms}
          viltDispModal={viltDispModal} setViltDispModal={setViltDispModal}
          viltShareCount={viltShareCount} setViltShareCount={setViltShareCount}
          viltAvpalsat={viltAvpalsat} setViltAvpalsat={setViltAvpalsat}
          viltSlaktvikt={viltSlaktvikt} setViltSlaktvikt={setViltSlaktvikt}
          viltTrikin={viltTrikin} setViltTrikin={setViltTrikin}
          viltCesium={viltCesium} setViltCesium={setViltCesium}
          viltSaleNotes={viltSaleNotes} setViltSaleNotes={setViltSaleNotes}
          viltFallavgift={viltFallavgift} setViltFallavgift={setViltFallavgift}
          viltKottkostnad={viltKottkostnad} setViltKottkostnad={setViltKottkostnad}
          viltVikt={viltVikt} setViltVikt={setViltVikt}
          viltSaving={viltSaving} setViltSaving={setViltSaving}
          showAddVilt={showAddVilt} setShowAddVilt={setShowAddVilt}
          addViltForm={addViltForm} setAddViltForm={setAddViltForm}
          handlingLogModal={handlingLogModal} setHandlingLogModal={setHandlingLogModal}
          handlingLog={handlingLog} setHandlingLog={setHandlingLog}
          handlingLogLd={handlingLogLd} setHandlingLogLd={setHandlingLogLd}
          showLabelPrinter={showLabelPrinter} setShowLabelPrinter={setShowLabelPrinter}
          labelPrintReg={labelPrintReg} setLabelPrintReg={setLabelPrintReg}
          user={user}
        />}
        {/* ── ALBUM ── */}
        {/* ── ALBUM ── */}
        {tab==='album'&&<AlbumTab
          albumLd={albumLd} albumImages={albumImages} setAlbumImages={setAlbumImages}
          albumViewer={albumViewer} setAlbumViewer={setAlbumViewer}
          isAdmin={isAdmin}
        />}

        {/* ── ANSÖKNINGAR ── */}
        {/* D5: ett ställe för ansökningar – antagningen (köp och ansökningar via annonser) och de äldre, skrivskyddade. */}
        {tab==='ansokningar'&&(isAdmin||myPerms?.canManageMembers||myPerms?.canEditMark)&&<>
          <AdmissionsPanel markId={mark.id} canEdit={!!(isAdmin||myPerms?.canEditMark)} isOwner={!!(isMarkOwner||isAdmin)}/>
          <LegacyApplications markId={mark.id}/>
        </>}


        {/* ── KONTRAKT ── */}
        {tab==='kontrakt'&&<KontraktTab
          mark={mark} user={user} isAdmin={isAdmin} myPerms={myPerms}
          showPubAvtal={showPubAvtal} setShowPubAvtal={setShowPubAvtal}
          pubAvtalSigned={pubAvtalSigned} setPubAvtalSigned={setPubAvtalSigned}
          pubAvtalSignName={pubAvtalSignName} setPubAvtalSignName={setPubAvtalSignName}
          T={T}
        />}

        {/* ── MARKENS UPPGIFTER ── */}
        {tab==='annons'&&(isAdmin||myPerms?.canEditMark)&&<MarkInfoTab
          mark={mark} setMark={setMark} form={annonsForm} setForm={setAnnonsForm}
          published={annonsPublished} onTogglePublished={toggleAnnonsPublished}
          err={annonsErr} ok={annonsOk} uploading={annonsUploading} saving={annonsSaving} onSave={saveAnnons}
          imageUrl={annonsImageUrl} setImageUrl={setAnnonsImageUrl} gallery={annonsGallery} setGallery={setAnnonsGallery}
          specOcc={annonsSpecOcc} setSpecOcc={setAnnonsSpecOcc} facilities={annonsFacilities} setFacilities={setAnnonsFacilities}
          imgRef={annonsImgRef} gallRef={annonsGallRef} videoRef={annonsVideoRef}
          onMainImg={handleAnnonsMainImg} onGallery={handleAnnonsGallery} onVideo={handleAnnonsVideo}
          onOpenEkonomi={()=>openEkonomi()}
        />}

        {/* ── EKONOMI ── */}
        {/* ── EKONOMI ── */}
        {tab==='ekonomi'&&(isAdmin||myPerms?.canEditMark||myPerms?.canManageMembers)&&<HuntPayments markId={mark.id} markName={mark.name}/>}
        {tab==='ekonomi'&&(isAdmin||myPerms?.canEditMark)&&<StripeConnectPanel markId={mark.id} canConnect={!!(isAdmin||isMarkOwner)}/>}
        {tab==='ekonomi'&&(isAdmin||myPerms?.canEditMark)&&<LodgingSettings markId={mark.id}/>}
        {tab==='ekonomi'&&<GroupPrices markId={mark.id} canEdit={!!(isAdmin||myPerms?.canEditMark)} focusGroupId={ekonomiFocus} reloadKey={costModel?.updatedAt}/>}
        {tab==='ekonomi'&&<EkonomiTab
          mark={mark} setMark={setMark}
          costLd={costLd} costModel={costModel} setCostModel={setCostModel}
          costSaving={costSaving} setCostSaving={setCostSaving}
          costErr={costErr} setCostErr={setCostErr}
          costForm={costForm} setCostForm={setCostForm}
          fallavgifter={fallavgifter} setFallavgifter={setFallavgifter}
          kottkostnad={kottkostnad} setKottkostnad={setKottkostnad}
          newFall={newFall} setNewFall={setNewFall}
          newKott={newKott} setNewKott={setNewKott}
          editMarkInfo={editMarkInfo} setEditMarkInfo={setEditMarkInfo}
          markInfoForm={markInfoForm} setMarkInfoForm={setMarkInfoForm}
          markInfoSaving={markInfoSaving} setMarkInfoSaving={setMarkInfoSaving}
          swishForm={swishForm} setSwishForm={setSwishForm}
          swishSaving={swishSaving} setSwishSaving={setSwishSaving}
          swishErr={swishErr} setSwishErr={setSwishErr}
          isAdmin={isAdmin} canEditPrices={!!(isAdmin||myPerms?.canEditMark)} T={T}
        />}
        {tab==='ekonomi'&&<ForbiddenGamePanel markId={mark.id} canEdit={!!(isAdmin||myPerms?.canEditMark)}/>}
        {/* ── EFTER JAKT (P4) OCH STATISTIK (P6) ── */}
        {tab==='efterjakt'&&<EfterJaktTab markId={mark.id} userEmail={user?.email}/>}
        {tab==='statistik'&&<StatistikTab markId={mark.id} center={mark.mapLat&&mark.mapLng?[mark.mapLat,mark.mapLng]:null}/>}
        {/* ── ANNONSER ── */}
        {tab==='annonser'&&(isAdmin||myPerms?.canEditMark)&&<AdsTab mark={mark} groups={groups} openAdId={initialAdId} onOpenEkonomi={openEkonomi} onOpenMarkInfo={()=>setTab('annons')} onMarkImageChanged={m=>{setMark(m);setAnnonsImageUrl(m.imageUrl||'');onMarkImageChanged?.(m);}}/>}
        {/* ── INTEGRATIONER ── */}
        {tab==='integrationer'&&(
          <div>
            <IntegrationPanel markId={mark.id} markLat={mark.mapLat} markLng={mark.mapLng} />
          </div>
        )}

        {/* ── KARTA ── */}
        {tab==='karta'&&(
          <div style={{paddingBottom:24,position:'relative'}}>
            <KartaTab
              mark={mark}
              isAdmin={!!isAdmin}
              canEditSatar={!!(isAdmin||myPerms?.canEditMark)&&!mark.isDemo}
              onSatarSaved={json=>setMark(m=>({...m,coordinatesJson:json}))}
              token={auth.getAccessToken()||''}
              onMarkUpdated={async(data)=>{
                const updated=await api.updateMark(mark.id,data);
                setMark(updated);
              }}
              variant="markDetail"
              joinableHuntMeetingId={joinableHuntMeetingIdVerified}
              onJoinOngoingHunt={async(meetingId:string)=>{await handleJoinFromChat(meetingId);}}
              onPlanHunt={handleOpenPlanFromMap}
              planOpen={showSatPlanerare}
              fullscreenRequest={mapFullscreenRequest!==mapFsHandledRef.current?mapFullscreenRequest:undefined}
              onFullscreenRequestDone={()=>{mapFsHandledRef.current=mapFullscreenRequest;}}
              huntMarks={huntMarks}
              onPickHuntMark={pickHuntMark}
              aside={<UpcomingCard markId={mark.id} onShowCalendar={()=>setTab('kalender')}/>}
              huntSession={huntSession}
              onStartHunt={handleSnabbstartMark}
              onEndHunt={async(liveTranscript,meta)=>{
                const session=huntSession;
                setHuntSession(null);
                setJoinableHuntMeetingIdVerified(null); // prevent reconnect-prompt on next start
                // End Qmeet meeting
                if(session?.meetingId){
                  endHuntMeeting(session.meetingId);
                }
                const startT=huntStartTimeRef.current||new Date().toISOString();
                const huntStartMs=huntStartTimeRef.current?new Date(huntStartTimeRef.current).getTime():0;
                const duration=huntStartTimeRef.current?Math.round((Date.now()-new Date(huntStartTimeRef.current).getTime())/60000):0;
                // Confirmed kills from hunt-chat subcat buttons (already saved to DB)
                const confirmedKills=(meta?.confirmedKills||[]).map((ck:{subcat:string;lat?:number;lng?:number;ts:number})=>({
                  type:'skjutet_vilt',label:ck.subcat,lat:ck.lat,lng:ck.lng,ts:ck.ts,kon:'Okänt',source:'bekräftad',
                }));
                // Build structured summary from transcript
                const parsedT=parseHuntTranscript(liveTranscript&&liveTranscript.length>0?liveTranscript.map(e=>`[${new Date(e.ts).toLocaleTimeString('sv-SE',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}] ${e.speaker}: ${e.text}`).join('\n'):'');
                const parsedEntries=liveTranscript&&liveTranscript.length>0?parseTranscriptEntries(liveTranscript):{antalDrev:parsedT.antalDrev,obsRows:[],killRows:[]};
                // QJ2: Q:s transkribering (alla deltagares telefoner + jaktchatten) och tolkade sammanfattning, om den finns.
                const qSummary=meta?.qSummary||null;
                const agentTranscription=qSummary?.transcript?qSummary.transcript:liveTranscript&&liveTranscript.length>0
                  ?liveTranscript.map(e=>{const t=new Date(e.ts).toLocaleTimeString('sv-SE',{hour:'2-digit',minute:'2-digit',second:'2-digit'});return `[${t}] ${e.speaker}: ${e.text}`;}).join('\n')
                  :`[Transkribering ej tillgänglig]\n\nMötet startade ${new Date(startT).toLocaleString('sv-SE')}.\nJakten avslutades efter ${duration} minuter.`;
                // Parse group chat messages sent since hunt started for kills/observations
                const huntChatText=messages.filter(m=>m.message&&new Date(m.createdAt).getTime()>=huntStartMs).map(m=>`${m.senderName}: ${m.message}`).join('\n');
                const parsedChat=huntChatText?parseHuntTranscript(huntChatText):{antalDrev:0,observations:[],kills:[]};
                if(!session?.calEventId){
                  // Snabbstart — include confirmed kills + transcript/chat
                  const confirmedLabels=new Set(confirmedKills.map((c:any)=>c.label));
                  const allKills=[...confirmedKills,...[...new Set([...parsedT.kills,...parsedChat.kills])].filter(l=>!confirmedLabels.has(l)).map(label=>({type:'skjutet_vilt',label,kon:'Okänt',source:'transkribering'}))];
                  const allKillLabels=new Set(allKills.map((k:any)=>k.label));
                  const allObs=[...new Set([...parsedT.observations,...parsedChat.observations].filter(o=>!allKillLabels.has(o)))];
                  const syntheticEvent:MarkCalendarEvent={
                    id:'snabbstart',markId:mark.id,title:session?.title||`Jakt — ${mark.name}`,
                    eventType:'hunt',startTime:startT,createdAt:startT,status:'completed',
                    endedAt:new Date().toISOString(),huntMeetingId:session?.meetingId,
                    transcription:agentTranscription,
                    summary:qSummary?.summary||[`Jakten på ${mark.name} pågick i ${duration} minuter.`,allKills.length>0?`Fällt vilt: ${allKills.map((k:any)=>k.label).join(', ')}.`:'',allObs.length>0?`Observationer: ${allObs.join(', ')}.`:''].filter(Boolean).join('\n'),
                    observationsJson:JSON.stringify(allObs.map(label=>({type:'observation',label}))),
                    killsJson:JSON.stringify(allKills),
                    invites:[],
                  };
                  setCompletedHuntDetail(syntheticEvent);
                  if(allKills.length>0)setShowUpdateGoals(true);
                  return;
                }
                // Complete calendar hunt — save real transcript + agent summary
                try{
                  const agentSummary=[
                    `Jakten genomfördes på ${mark.name} och pågick i ${duration} minuter.`,
                    parsedT.antalDrev>0?`Antal drev: ${parsedT.antalDrev} drev genomförda.`:'',
                    parsedT.observations.length>0?`Observerat vilt: ${parsedT.observations.join(', ')}.`:'',
                    confirmedKills.length>0?`Fällt vilt (bekräftat): ${confirmedKills.map((c:any)=>c.label).join(', ')}.`:'',
                    parsedT.kills.length>0?`Fällt vilt (transkribering): ${parsedT.kills.join(', ')}.`:'',
                    parsedChat.kills.length>0?`Fällt vilt (chatt): ${parsedChat.kills.join(', ')}.`:'',
                  ].filter(Boolean).join('\n');
                  // Collect map pins added DURING the hunt (filter by hunt start time)
                  const allHuntPins=await fetch(resolveUrl('/graphql'),{method:'POST',headers:{'Content-Type':'application/json','X-Api-Key':'dev-secret',...bearerHeaders(auth.getAccessToken())},body:JSON.stringify({query:`query{markPins(markId:"${mark.id}"){id type label latitude longitude createdAt}}`})}).then(r=>r.json()).then(d=>d?.data?.markPins||[]).catch(()=>[]);
                  const huntPins=allHuntPins.filter((p:any)=>new Date(p.createdAt).getTime()>=huntStartMs);
                  const obsItems=huntPins.filter((p:any)=>p.type==='observation').map((p:any)=>({type:p.type,label:p.label||'Observation',lat:p.latitude,lng:p.longitude,ts:new Date(p.createdAt).getTime()}));
                  const pinKillItems=huntPins.filter((p:any)=>p.type==='skjutet_vilt'||p.type==='fallt_vilt').map((p:any)=>({type:'skjutet_vilt',label:p.label||'Fällt vilt',lat:p.latitude,lng:p.longitude,ts:new Date(p.createdAt).getTime(),kon:'Okänt',source:'karta'}));
                  // Transcript & chat (unconfirmed)
                  const transcriptObsItems=[
                    ...parsedEntries.obsRows.map(r=>({type:'observation',label:r.art,lat:r.lat,lng:r.lng,speaker:r.speaker,ts:r.ts})),
                    // Skott som telefonens ljudmätning hörde, med jägarens position vid skottet
                    ...(liveTranscript||[]).filter(isShotEntry).map(e=>({type:'observation',label:'Skott',lat:e.lat,lng:e.lng,speaker:e.speaker,ts:e.ts})),
                  ];
                  const transcriptKillItems=parsedEntries.killRows.map(r=>({type:'skjutet_vilt',label:r.art,lat:r.lat,lng:r.lng,speaker:r.speaker,ts:r.ts,kon:r.kon,source:'transkribering'}));
                  const chatKillItems=parsedChat.kills.map(label=>({type:'skjutet_vilt',label,kon:'Okänt',source:'transkribering'}));
                  // Build final lists: confirmed first (always kept), then unconfirmed deduplicated
                  const confirmedLabels=new Set(confirmedKills.map((c:any)=>c.label));
                  const allObsItems=[...obsItems,...transcriptObsItems.filter(t=>!obsItems.some((o:any)=>o.label===t.label&&Math.abs((o.lat||0)-(t.lat||0))<0.001))];
                  const allKillItems=[
                    ...confirmedKills,
                    ...pinKillItems.filter((p:any)=>!confirmedLabels.has(p.label)),
                    ...transcriptKillItems.filter(t=>!confirmedLabels.has(t.label)&&!pinKillItems.some((p:any)=>p.label===t.label&&Math.abs((p.lat||0)-(t.lat||0))<0.001)),
                    ...chatKillItems.filter((c:any)=>!confirmedLabels.has(c.label)&&!pinKillItems.some((p:any)=>p.label===c.label)&&!transcriptKillItems.some(t=>t.label===c.label)),
                  ];
                  const completed=await api.completeHunt(session.calEventId,{
                    summary:qSummary?.summary||agentSummary,
                    transcription:agentTranscription,
                    observationsJson:JSON.stringify(allObsItems),
                    killsJson:JSON.stringify(allKillItems),
                  });
                  api.markCalendarEvents(mark.id,true).then(evs=>setEvents(evs)).catch(()=>{});
                  setCompletedHuntDetail({...completed,huntMeetingId:session.meetingId});
                  if(allKillItems.length>0)setShowUpdateGoals(true);
                }catch(err){console.error('[QVENARI] completeHunt error',err);}
              }}
              isJaktledare={!!isAdmin||myPerms?.canManageShootingPlan||false}
              userName={huntDisplayName(user)}
              userAvatarUrl={userAvatarUrl||undefined}
              onLeaveHunt={()=>setHuntSession(null)}
              onViltRegistered={(reg)=>setViltRegs(prev=>[...prev,reg])}
            />
            {/* Album under kartan: de fyra senaste bilderna, resten under Mer → Album */}
            <div style={{marginTop:20}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                <div style={{...secT,marginBottom:0}}>Album</div>
                {albumImages.length>0&&<button onClick={()=>setTab('album')} style={{background:'none',border:'none',color:T.amber,fontSize:13,fontWeight:700,cursor:'pointer',padding:'4px 0',fontFamily:'inherit'}}>Visa alla ({albumImages.length}) →</button>}
              </div>
              {albumLd&&albumImages.length===0?<div style={{color:T.textMuted,fontSize:13}}>Laddar bilder…</div>
              :albumImages.length===0?<div style={{color:T.textMuted,fontSize:13}}>Inga bilder ännu — bilder från jaktchatten och gruppchatten hamnar här.</div>
              :<div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:6}}>
                {[...albumImages].map((img,i)=>({img,i})).sort((a,b)=>new Date(b.img.createdAt).getTime()-new Date(a.img.createdAt).getTime()).slice(0,4).map(({img,i})=>(
                  <button key={img.id} onClick={()=>{setTab('album');setAlbumViewer(i);}} title={img.caption||'Bild'} style={{aspectRatio:'1',padding:0,border:`1px solid ${T.border}`,borderRadius:6,overflow:'hidden',cursor:'pointer',background:T.surfaceHover}}>
                    <img src={resolveUrl(img.imageUrl)} alt={img.caption||'Bild'} loading="lazy" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
                  </button>
                ))}
                <button onClick={()=>setTab('album')} style={{aspectRatio:'1',border:`1px solid ${T.amber}`,borderRadius:6,background:'transparent',color:T.amber,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:2}}>
                  <span style={{fontSize:18,lineHeight:1}}>+</span>Mer
                </button>
              </div>}
            </div>
          </div>
        )}

        {/* Completed hunt modal after ending a hunt */}
        {completedHuntDetail&&<CompletedHuntModal event={completedHuntDetail} onClose={()=>{setCompletedHuntDetail(null);setShowUpdateGoals(false)}} onUpdateGoals={isDemo?undefined:async(kills)=>{
          try{
            const todayIso=new Date().toISOString().slice(0,10);
            // Hämta planen färskt: plan-state laddas bara när fliken Avskjutningsplan har öppnats
            const plan=await api.markShootingPlan(mark.id);
            for(const k of kills){
              // Find exact plan row match first, then fall back to family-key match
              let existing=plan.find(p=>p.gameType===k.gameType);
              if(!existing){
                const wantFk=planSpeciesFamilyKey(k.gameType);
                existing=plan.find(p=>planSpeciesFamilyKey(p.gameType)===wantFk);
              }
              if(existing){
                const newActual=(existing.actualCount||0)+k.count;
                await api.upsertMarkShootingPlan({markId:mark.id,season:existing.season,gameType:existing.gameType,plannedCount:existing.plannedCount,actualCount:newActual,costPerAnimal:existing.costPerAnimal,notes:`Uppdaterad efter jakt ${new Date().toLocaleDateString('sv-SE')} (+${k.count} fällt)`});
              }
              // Create viltRegistration entries so kills appear in vilthantering/viltkött
              // Use family-level name (e.g. "Vildsvin") so it matches MEAT_SPECIES
              const viltRegGt=k.gameType.includes(' — ')?k.gameType.split(' — ')[0]:k.gameType;
              // EJ5: djuren kopplas till jaktens rapport, så att jakten inte får en rad under Efter jakt
              const meetingId=completedHuntDetail.huntMeetingId??completedHuntDetail.qmeetMeetingId;
              for(let i=0;i<k.count;i++){
                await api.addViltRegistration({markId:mark.id,season:planSeason,gameType:viltRegGt,shotDate:todayIso,meetingId}).catch(()=>{});
              }
            }
            notifyHuntEconomyChanged();
            // Refresh plan and viltRegistrations
            setPlanLd(true);api.markShootingPlan(mark.id).then(d=>{setPlan(d);setPlanLd(false)}).catch(()=>setPlanLd(false));
            api.markViltRegistrations(mark.id).then(d=>setViltRegs(d)).catch(()=>{});
            setShowUpdateGoals(false);
          }catch(err){console.error('Goal update error',err)}
        }}/>}

        {/* ── JAKTINBJUDAN (J1) ── */}
        {calDetailEvent&&(
          <InvitationDialog event={calDetailEvent} mark={mark}
            canEdit={!!(isAdmin||myPerms?.canManageCalendar||(user?.email&&calDetailEvent.createdBy?.toLowerCase()===user.email.toLowerCase()))}
            onClose={()=>setCalDetailEvent(null)}
            onEdit={ev=>{setCalDetailEvent(null);setPlanEditEvent(ev);setShowSatPlanerare(true);}}
            onChanged={ev=>setEvents(prev=>prev.map(e=>e.id===ev.id?ev:e))}/>
        )}

        {/* ── PLANERA JAKT (J1) ── */}
        {showSatPlanerare&&mark&&(
          <PlaneraJakt mark={mark} members={members} user={user} editEvent={planEditEvent}
            onDone={ev=>{
              setEvents(prev=>[...prev.filter(e=>e.id!==ev.id),ev].sort((a,b)=>new Date(a.startTime).getTime()-new Date(b.startTime).getTime()));
              if(!planEditEvent)onCalEventCreated?.(ev);
              setCalTitle('');setCalStart('');setCalEnd('');setCalSatPlan(undefined);
            }}
            onClose={()=>{setShowSatPlanerare(false);setPlanEditEvent(null);setTab('kalender');}}
          />
        )}

      </div>
    </div>
  );
}

// ── APP ROOT ─────────────────────────────────────────────────────

function AppInner(){
  const[themeMode,setThemeMode]=useState<ThemeMode>(()=>(localStorage.getItem('qvenari_theme') as ThemeMode)||'light');
  const toggleTheme=useCallback(()=>setThemeMode(m=>{const n=m==='dark'?'light':'dark';localStorage.setItem('qvenari_theme',n);return n}),[]);
  // iOS-appen: sidan ligger under statusfältet, så klockan/batteriet följer temat (mörk text på ljus meny).
  useEffect(()=>{
    if(!(window as any).Capacitor?.isNativePlatform?.())return;
    StatusBar.setStyle({style:themeMode==='dark'?Style.Dark:Style.Light}).catch(()=>{});
  },[themeMode]);
  T=getTheme(themeMode);

  // Göm Capacitor SplashScreen direkt när AppInner mountas
  useEffect(() => { hideSplash(); }, []);
  const[av,setAv]=useState<View>('marketplace');
  const[mob,setMob]=useState(false);
  const[menuOpen,setMenuOpen]=useState(false); // kept for legacy onMer
  const[af,setAf]=useState('Alla');
  const[markOpen,setMarkOpen]=useState(false);
  const[user,setUser]=useState<QidUser|null>(null);
  // Q (röst-/chattassistenten, src/q/): loggan i toppraden öppnar panelen; ett långt tryck ökar qListen och Q lyssnar direkt.
  const[qOpen,setQOpen]=useState(false);
  const[qListen,setQListen]=useState(0);
  const openQ=(listen:boolean)=>{setQOpen(true);if(listen)setQListen(n=>n+1);};
  // Android: bakåtknappen stänger Q och menyn innan sidan går bakåt (androidApp.ts).
  useEffect(()=>{initAndroidBackButton();},[]);
  // A3: hämtar jaktbilder automatiskt när appen öppnas, om jägaren slagit på det (huntMediaSync.ts).
  useEffect(()=>startHuntMediaAutoSync(),[]);
  useEffect(()=>onAndroidBack(()=>{if(qOpen){setQOpen(false);return true}if(menuOpen){setMenuOpen(false);return true}return false}),[qOpen,menuOpen]);
  const[isAdmin,setIsAdmin]=useState(false);
  const[userAvatarUrl,setUserAvatarUrl]=useState<string|null>(null);
  const[authLoading,setAuthLoading]=useState(false);
  const[authErr,setAuthErr]=useState<string|null>(null);
  const[showLogin,setShowLogin]=useState(false);
  // /?oauth=<id>: en MCP-klient (t.ex. Claude.ai) ber om åtkomst. Sparas så att den överlever inloggning via Qid.
  const[oauthRequestId,setOauthRequestId]=useState<string|null>(()=>{
    const fromUrl=new URLSearchParams(window.location.search).get('oauth');
    try{
      if(fromUrl){sessionStorage.setItem('qvenari_oauth_request',fromUrl);return fromUrl;}
      return sessionStorage.getItem('qvenari_oauth_request');
    }catch{return fromUrl;}
  });
  useEffect(()=>{
    const p=new URLSearchParams(window.location.search);
    if(p.has('oauth')){p.delete('oauth');const q=p.toString();window.history.replaceState({},'',window.location.pathname+(q?`?${q}`:''));}
  },[]);
  const closeOauth=()=>{try{sessionStorage.removeItem('qvenari_oauth_request')}catch{};setOauthRequestId(null);};
  const[cart,setCart]=useState<CartItem[]>([]);
  const[co,setCo]=useState(false);
  // Startsidan är Jakt (som Plagg på Qvestium); Min jakt motsvarar Min garderob.
  // UX12: inloggad med egen jaktmark → Min jakt (direkt om det var så förra gången, annars när myMarks svarat).
  const plainStartRef=useRef(isPlainStart(window.location.pathname,window.location.search));
  const optimisticMinJaktRef=useRef(initialMpTab(plainStartRef.current,!!auth.getUser()&&auth.isLoggedIn(),rememberedMinJakt(auth.getUser()?.sub))==='minajakt');
  const[mpTab,setMpTab]=useState<'minajakt'|'jakt'|'arrende'>(optimisticMinJaktRef.current?'minajakt':'jakt');
  /** Räknas upp vid varje klick i flikraden — marknaden stänger då öppen jaktsida/sökresultat. */
  const[mpNavTick,setMpNavTick]=useState(0);
  useEffect(()=>{if(av!=='marketplace')setCo(false)},[av]);

  // ── Invite link → open mark detail page ─────────────────────────
  const[inviteToken,setInviteToken]=useState<string|null>(()=>{const p=new URLSearchParams(window.location.search);const t=p.get('invite')||null;if(t)rememberMarkInvite(t);/* IK1: släpper in utan invitekod */return t;});
  const[inviteMark,setInviteMark]=useState<Mark|null>(null);
  const[inviteLoading,setInviteLoading]=useState(false);
  const[inviteErr,setInviteErr]=useState('');
  const[inviteNeedsLogin,setInviteNeedsLogin]=useState(false);
  useEffect(()=>{
    if(!inviteToken)return;
    window.history.replaceState({},'',window.location.pathname);
    setInviteLoading(true);setInviteErr('');
    api.inviteLinkPreview(inviteToken).then(async p=>{
      if(!p.isValid){setInviteErr('Inbjudningslänken är inte längre giltig.');setInviteLoading(false);return;}
      try{const m=await api.markById(p.markId);setInviteMark(m);setInviteLoading(false);}catch{setInviteErr('Kunde inte ladda jaktmarken.');setInviteLoading(false);}
    }).catch(()=>{setInviteErr('Inbjudningslänken är ogiltig.');setInviteLoading(false);});
  },[inviteToken]);

  // ── Label view via QR code scan (?labelId=...) ───────────────────
  // ET6: visas av LabelView; även äldre /v/:id-länkar från utrustningsetiketter
  const[labelViewId,setLabelViewId]=useState<string|null>(()=>labelIdFromUrl(window.location.href));
  useEffect(()=>{
    if(!labelViewId)return;
    window.history.replaceState({},'',window.location.pathname.startsWith('/v/')?'/':window.location.pathname);
  },[labelViewId]);

  // ── Join meeting via QR / link ───────────────────────────────────
  const[joinMeetingId,setJoinMeetingId]=useState<string|null>(()=>{
    const p=new URLSearchParams(window.location.search);
    const v=p.get('join')||null;
    // Also check sessionStorage (survives login redirect)
    if(!v){const s=sessionStorage.getItem('qvenari_pending_join');if(s){try{return JSON.parse(s).meetingId||null;}catch(e){}}}
    return v;
  });
  const[joinMarkId,setJoinMarkId]=useState<string|null>(()=>{
    const p=new URLSearchParams(window.location.search);
    const v=p.get('mark')||null;
    // Also check sessionStorage (survives login redirect)
    if(!v){const s=sessionStorage.getItem('qvenari_pending_join');if(s){try{return JSON.parse(s).markId||null;}catch(e){}}}
    return v;
  });
  const[joinGuestName,setJoinGuestName]=useState(()=>{
    try{
      const p=new URLSearchParams(typeof window!=='undefined'?window.location.search:'');
      return (p.get('namn')||p.get('name')||p.get('guest')||'').trim();
    }catch{return '';}
  });
  const[joinGuestNameInput,setJoinGuestNameInput]=useState('');
  const[joinGuestNameConfirmed,setJoinGuestNameConfirmed]=useState(()=>{
    try{
      const p=new URLSearchParams(typeof window!=='undefined'?window.location.search:'');
      return !!(p.get('namn')||p.get('name')||p.get('guest'));
    }catch{return false;}
  });
  // GU1: gäst utan inloggning — kod via ?kod=… eller sidan /anslut, eller väntrum som överlevt en omladdning.
  const[guestCode,setGuestCode]=useState<string|null>(()=>{try{return new URLSearchParams(window.location.search).get('kod');}catch{return null;}});
  const[guestCodeMode,setGuestCodeMode]=useState(()=>{try{return window.location.pathname==='/anslut'||!!new URLSearchParams(window.location.search).get('kod')||!!loadGuestWait();}catch{return false;}});
  const guestCredsRef=useRef<GuestWait|null>(null);
  const[guestNotice,setGuestNotice]=useState('');
  const[joinBusy,setJoinBusy]=useState(false);
  const[joinErr,setJoinErr]=useState('');
  const[joinActiveMeeting,setJoinActiveMeeting]=useState<ActiveMeeting|null>(null);
  const[joinLeaderVideo,setJoinLeaderVideo]=useState(false);
  const[joinPendingMark,setJoinPendingMark]=useState<Mark|null>(null);
  const joinQrAutoRef=useRef(false);

  async function handleJoin(nameOverride?:string){
    if(!joinMeetingId)return;
    const name=(nameOverride||user?.name||joinGuestName||'Gäst').trim();
    if(!name){setJoinErr('Ange ditt namn');return;}
    setJoinBusy(true);setJoinErr('');
    try{
      const identity=name.toLowerCase().replace(/\s+/g,'-')+'-'+Date.now().toString(36);
      const jr=await fetch(qmeetApi(`/api/meetings/${joinMeetingId}/join`),{
        method:'POST',headers:{'Content-Type':'application/json',...bearerHeaders()},
        body:JSON.stringify({identity,name})
      });
      if(!jr.ok){const e=await jr.json().catch(()=>({}));throw new Error((e as any).error||`HTTP ${jr.status}`);}
      const res=await jr.json() as { token: string; meeting_title?: string; livekit_url?: string };
      const isCapLink = !!(window as any).Capacitor?.isNativePlatform?.();
      const jLkUrl = resolveLivekitWsUrl(res, isCapLink);
      console.log('[QVENARI-MEET] Join — LiveKit URL selected', { livekitUrl: jLkUrl, fromQmeet: !!res.livekit_url });
      setJoinActiveMeeting({token:res.token,livekitUrl:jLkUrl,title:res.meeting_title||'Jakten',isLeader:false});
      window.history.replaceState({},'',window.location.pathname);
    }catch(e:unknown){setJoinErr((e as Error).message||'Kunde inte ansluta');}
    setJoinBusy(false);
  }

  /** GU1: gästen är insläppt — gästtoken till api.ts, radion från gäst-API:t, sedan samma jaktvy som deltagarna. */
  async function onGuestJoined(a:GuestApproved,wait:GuestWait){
    setGuestHunt({meetingId:a.hunt.meetingId,token:a.guestToken});
    guestCredsRef.current=wait;
    try{
      // Utan radio (Qmeet nere eller mötet tappat) ser gästen ändå kartan, positionerna och chatten.
      const v=await guestApi.voice().catch(e=>{console.warn('[GU1] radion kunde inte startas',e);return{livekitToken:'',livekitUrl:null,identity:'',title:a.hunt.title};});
      const isCap=!!(window as any).Capacitor?.isNativePlatform?.();
      setJoinGuestName(a.name);
      setJoinMeetingId(a.hunt.meetingId);setJoinMarkId(a.hunt.markId);
      setJoinPendingMark({...a.mark} as unknown as Mark);
      setJoinActiveMeeting({token:v.livekitToken,livekitUrl:resolveLivekitWsUrl({livekit_url:v.livekitUrl||undefined},isCap),title:v.title||a.hunt.title,isLeader:false});
      setGuestCodeMode(false);setGuestCode(null);
      window.history.replaceState({},'','/');
    }catch(e){
      leaveGuestHunt((e as Error).message||'Kunde inte ansluta till radion');
    }
  }
  /** Gästen lämnar eller stängs ute (jakten slut, borttagen). */
  function leaveGuestHunt(notice=''){
    const w=guestCredsRef.current;
    if(w&&!notice)void guestApi.cancel(w.requestId,w.secret).catch(()=>{});
    guestCredsRef.current=null;saveGuestWait(null);setGuestHunt(null);
    setJoinActiveMeeting(null);setJoinMeetingId(null);setJoinMarkId(null);setJoinPendingMark(null);
    setGuestNotice(notice);
  }

  // QR/länk ?join=… — auto-anslut när auth är klar.
  // Gäster (ej inloggade) ansluter direkt via knappen ovan (handleJoin anropas med namn).
  // Inloggad utan mark-param: auto-anslut. Inloggad + mark → MyMarkDetailView.
  useEffect(()=>{
    if(!joinMeetingId||joinActiveMeeting||joinBusy)return;
    if(joinQrAutoRef.current)return;
    if(authLoading)return;
    if(!user)return; // gäst ansluter via knappen, inte via effect
    if(user&&joinMarkId)return; // inloggad + mark → MyMarkDetailView
    joinQrAutoRef.current=true;
    void handleJoin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[joinMeetingId,joinMarkId,joinActiveMeeting,joinBusy,authLoading,user?.sub]);

  /** Poll meeting status for guest (joinActiveMeeting) — auto-close when hunt ends. */
  useEffect(()=>{
    const mid=joinMeetingId;
    if(!mid||!joinActiveMeeting)return;
    let cancelled=false;
    // Gäst: gäst-API:t svarar 401 när jakten är slut eller gästen tagits bort. Tokenen förnyas var 20:e minut.
    if(guestCredsRef.current){
      const creds=guestCredsRef.current;
      const gcheck=async()=>{
        try{await guestApi.hunt();}
        catch(e){if(!cancelled&&e instanceof GuestApiError&&e.status===401)leaveGuestHunt('Jakten har avslutats, eller så har du tagits bort ur den.');}
      };
      const refresh=async()=>{
        try{const s=await guestApi.status(creds.requestId,creds.secret);if(!cancelled&&s.status==='approved')setGuestHunt({meetingId:mid,token:(s as GuestApproved).guestToken});}catch{}
      };
      const t1=setInterval(gcheck,15000);const t2=setInterval(refresh,20*60_000);
      return()=>{cancelled=true;clearInterval(t1);clearInterval(t2);};
    }
    const check=async()=>{
      try{
        const r=await fetch(qmeetApi(`/api/meetings/${mid}`),{headers:{...bearerHeaders()}});
        if(cancelled)return;
        if(r.status===404||!r.ok){if(r.status===404){setJoinActiveMeeting(null);setJoinMeetingId(null);}return;}
        const m=await r.json() as {status?:string};
        if(cancelled)return;
        if(m.status==='ended'){setJoinActiveMeeting(null);setJoinMeetingId(null);setJoinPendingMark(null);}
      }catch{}
    };
    void check();
    const tid=setInterval(check,15000);
    return()=>{cancelled=true;clearInterval(tid);};
  },[joinMeetingId,joinActiveMeeting]);

  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    // Handle Stripe checkout result
    const checkout=params.get('checkout');
    const kind=params.get('kind');
    const ref=params.get('ref')||params.get('order');
    if(checkout==='success'){
      window.history.replaceState({},'',window.location.pathname);
      if(kind==='ad_purchase'&&ref){
        // Köp av annons: kortet är reserverat, inte draget. Fråga API:t (som frågar Stripe) direkt.
        api.confirmAdPurchase(ref)
          .then(p=>{window.dispatchEvent(new Event(PURCHASE_UPDATED_EVENT));void askForPush();return p;})
          .then(p=>alert(p?.status==='reserved'?'Kortet är reserverat ✓\nDu väntar nu på att marken godkänner dig. Inget dras förrän du är godkänd – annars släpps reservationen.':'Betalningen registreras. Status syns under Min Jakt → Mina köp.'))
          .catch(()=>alert('Betalningen registreras. Status syns under Min Jakt → Mina köp.'));
      }else if(kind==='lodging_booking'&&ref){
        // Boende (L1): dras direkt. Fråga API:t (som frågar Stripe) så att bokningen syns direkt.
        api.confirmLodgingBooking(ref)
          .then(b=>alert(b?.status==='paid'?`Boendet är bokat och betalt ✓\n${b.lodgingName??'Boendet'} ${b.checkIn} – ${b.checkOut}. Bokningen syns under Min Jakt → Mina köp.`:'Betalningen registreras. Status syns under Min Jakt → Mina köp.'))
          .catch(()=>alert('Betalningen registreras. Status syns under Min Jakt → Mina köp.'));
      }else if(kind==='hunt_charge'&&ref){
        // P4: fällavgifter efter jakt – dras direkt. Fråga API:t (som frågar Stripe) så att statusen syns direkt.
        huntApi.confirmHuntChargePayment(ref)
          .then(c=>{notifyHuntEconomyChanged();if(c){try{sessionStorage.setItem('qvenari_open_mark_tab',JSON.stringify({markId:c.markId,tab:'efterjakt'}));}catch{}setAv('marketplace');setMpTab('minajakt');}alert(c?.status==='paid'?`Betalt ✓\n${krText(c.totalKr)} för ${c.huntTitle??'jakten'} ${c.huntDate??''}.`:'Betalningen registreras. Status syns under Efter jakt på marken.');})
          .catch(()=>alert('Betalningen registreras. Status syns under Efter jakt på marken.'));
      }else if(kind==='hunt_booking'){
        alert('Betalning genomförd! 🎉\nDin jakt är bokad. Du får en bekräftelse via e-post.');
      }else if(kind==='mark_membership'){
        alert('Betalning genomförd! 🎉\nDitt medlemskap aktiveras så snart betalningen registrerats.');
        setAv('marketplace');setMpTab('minajakt');
      }else{
        alert(`Betalning genomförd! 🎉\nReferens ${ref?.slice(0,8)||''}. Du får en bekräftelse via e-post.`);
      }
    } else if(checkout==='cancel'){
      window.history.replaceState({},'',window.location.pathname);
    }
    // Tillbaka från Stripe Connect-onboarding: uppdatera kontots status och öppna markens Ekonomi-flik.
    const connect=params.get('stripe_connect');const connectMark=params.get('stripe_mark');
    if(connect&&connectMark){
      window.history.replaceState({},'',window.location.pathname);
      try{sessionStorage.setItem('qvenari_open_mark_tab',JSON.stringify({markId:connectMark,tab:'ekonomi'}));}catch{}
      setAv('marketplace');setMpTab('minajakt');
      if(connect==='return')api.refreshStripeAccount(connectMark).then(st=>alert(st.chargesEnabled?'Stripe-kontot är kopplat ✓\nJägare kan nu köpa markens annonser.':'Stripe-kontot är inte klart ännu. Slutför uppgifterna under Ekonomi → Kortbetalning.')).catch(()=>{});
    }
    // AD1: tillbaka från Fortnox-kopplingen (API:t /api/admin/fortnox/callback) – öppna Admin → Bokföring.
    const fortnox=params.get('fortnox');
    if(fortnox){
      window.history.replaceState({},'',window.location.pathname);
      try{sessionStorage.setItem('qvenari_fortnox_result',fortnox);sessionStorage.setItem('qvenari_admin_tab','bokforing');}catch{}
      setAv('admin');
    }
    // P4: länk i påminnelsemejlet (?mark=…&tab=efterjakt) – öppna markens flik när markerna laddats.
    const linkMark=params.get('mark');const linkTab=params.get('tab');
    if(linkMark&&linkTab&&['efterjakt','statistik','ekonomi'].includes(linkTab)){
      window.history.replaceState({},'',window.location.pathname);
      try{sessionStorage.setItem('qvenari_open_mark_tab',JSON.stringify({markId:linkMark,tab:linkTab}));}catch{}
      setAv('marketplace');setMpTab('minajakt');
    }
    const code=params.get('code');const state=params.get('state');
    if(code&&state){
      setAuthLoading(true);
      window.history.replaceState({},'',window.location.pathname==='/auth/callback'?'/':window.location.pathname);
      auth.handleCallback(code,state)
          .then(u=>{setUser(u);setAuthLoading(false);goMinJaktIfMarks({stayOnInvite:!!joinMarkId||window.location.pathname.startsWith('/inbjudan/')})})
        .catch(e=>{if(!(e instanceof InviteRequiredError))setAuthErr('Inloggning misslyckades: '+String(e));setAuthLoading(false)});
    } else {
      const u=auth.getUser();
      if(u&&auth.isLoggedIn()){
        auth.fetchMe().then(me=>{if(me){setUser(me);startOnMinJakt();}else{if(!inviteCodePending())auth.clearTokens();setUser(null);leaveOptimisticMinJakt();}}).catch(()=>{auth.clearTokens();setUser(null);leaveOptimisticMinJakt();});
      }
    }
  },[]);

  // Pushnotiser (push.ts): registrera telefonen om användaren redan sagt ja — frågar inte här.
  useEffect(()=>{void initPush(user?.email??null);},[user?.email]);

  // Tryck på en pushnotis: öppna marken/fliken, jakten, inbjudan eller mina köp.
  useEffect(()=>{
    const onOpen=(e:Event)=>{
      const target=(e as CustomEvent<PushTarget>).detail;
      const action=openActionFor(target,_globalHunt.current?.session.meetingId??null);
      if(action.type==='navigate'){window.location.assign(action.url);return;}
      setShowLogin(false);setAv('marketplace');setMpTab('minajakt');
      if(action.type==='purchases'){setMpNavTick(t=>t+1);return;}
      // P4: notisen om fällt vilt pekar på en viss jakt – Efter jakt visar den först.
      if(action.tab==='efterjakt'&&target.meetingId){try{sessionStorage.setItem(EFTERJAKT_FOCUS_KEY,target.meetingId);}catch{}}
      // VMarketplace öppnar marken när den är laddad (eller direkt om den redan finns).
      try{sessionStorage.setItem('qvenari_open_mark_tab',JSON.stringify({markId:action.markId,tab:action.tab}));}catch{}
      window.dispatchEvent(new CustomEvent(OPEN_MARK_TAB_EVENT,{detail:{markId:action.markId,tab:action.tab,huntChat:action.huntChat}}));
    };
    window.addEventListener(PUSH_OPEN_EVENT,onOpen);
    return()=>window.removeEventListener(PUSH_OPEN_EVENT,onOpen);
  },[]);

  // Apple Watch: ge klockan ett eget token när användaren är inloggad (watchBridge.ts).
  useEffect(()=>{
    if(!user?.email)return;
    return initWatchBridge({email:user.email,name:user.name});
  },[user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch mark for join link — always (guest and logged-in). Navigation to minajakt only for logged-in users.
  useEffect(()=>{
    if(!joinMarkId||!joinMeetingId)return;
    if(user&&(joinActiveMeeting||joinQrAutoRef.current))return; // logged-in: already joined
    // Always fetch mark so guest flow can show KartaTab
    fetch(resolveUrl('/graphql'),{method:'POST',headers:{'Content-Type':'application/json','X-Api-Key':'dev-secret'},body:JSON.stringify({query:`query($id:ID!){mark(id:$id){id name location areal imageUrl latitude longitude coordinatesJson gameTypesJson isPublished gameTypes region}}`,variables:{id:joinMarkId}})})
      .then(r=>r.json())
      .then(d=>{
        const m=d?.data?.mark;
        if(!m)return;
        setJoinPendingMark(m);
        if(user){
          // Logged-in: persist to sessionStorage and navigate to mark detail
          try{sessionStorage.setItem('qvenari_pending_join',JSON.stringify({meetingId:joinMeetingId,markId:joinMarkId}));}catch(e){}
          setAv('marketplace');setMpTab('minajakt');window.history.replaceState({},'',window.location.pathname);
        }
      })
      .catch(()=>{});
  },[joinMarkId,joinMeetingId,user]); // eslint-disable-line

  useEffect(()=>{
    const ADMIN_EMAILS=['lasse@qvenari.se'];
    if(user){
      // After login: restore pending join from sessionStorage (survives Qid OIDC redirect)
      const stored=sessionStorage.getItem('qvenari_pending_join');
      if(stored&&!joinMarkId){
        try{
          const{meetingId,markId}=JSON.parse(stored);
          if(meetingId&&markId){
            sessionStorage.removeItem('qvenari_pending_join');
            setJoinMeetingId(meetingId);setJoinMarkId(markId);
            fetch(resolveUrl('/graphql'),{method:'POST',headers:{'Content-Type':'application/json','X-Api-Key':'dev-secret'},body:JSON.stringify({query:`query($id:ID!){mark(id:$id){id name location areal imageUrl latitude longitude isPublished gameTypes region}}`,variables:{id:markId}})})
              .then(r=>r.json())
              .then(d=>{const m=d?.data?.mark;if(m){setJoinPendingMark(m);setAv('marketplace');setMpTab('minajakt');}})
              .catch(()=>{});
          }
        }catch(e){}
      }
      // Köp som påbörjades utloggad: tillbaka till annonsen, där BuyAdButton fortsätter köpet.
      const pendingBuy=peekPendingPurchase();
      if(pendingBuy&&!window.location.pathname.startsWith('/annons/')){
        window.history.replaceState(null,'',`/annons/${encodeURIComponent(pendingBuy.adId)}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        setAv('marketplace');
      }
      // Derive isAdmin from user email directly (fast, no network call needed)
      const emailAdmin=ADMIN_EMAILS.includes((user.email||'').toLowerCase());
      if(emailAdmin){setIsAdmin(true);}
      const tok=localStorage.getItem('qvenari_access_token');
      if(tok){
        apiFetch('/api/profile',{headers:{...bearerHeaders(tok)}})
          .then(r=>r.ok?r.json():null)
          .then(d=>{
            if(d?.avatar_url)setUserAvatarUrl(d.avatar_url);else setUserAvatarUrl(null);
            setIsAdmin(!!(d?.is_admin)||emailAdmin);
          })
          .catch(()=>{setUserAvatarUrl(null);setIsAdmin(emailAdmin);});
      }
    }else{setUserAvatarUrl(null);setIsAdmin(false);}
  },[user]);

  useEffect(()=>{const c=()=>{setMob(window.innerWidth<768);if(window.innerWidth>=768)setMenuOpen(false)};c();window.addEventListener('resize',c);return()=>window.removeEventListener('resize',c)},[]);

  const hNav=(v:View)=>{setAv(v);setMenuOpen(false)};
  const hLogin=()=>{setAuthErr(null);setShowLogin(true)};
  // Siri-genvägen "Prata med Q i Qvenari" (q/qLaunch.ts): toppradens Q börjar lyssna, eller inloggningen om man är utloggad.
  // Under jakt tar jaktens lilla Q-kort anropet i stället (KartaTab registrerar sig ovanpå).
  const qLaunchRef=useRef<()=>void>(()=>{});
  qLaunchRef.current=()=>{if(user)openQ(true);else hLogin();};
  useEffect(()=>{const off=onQLaunch(()=>qLaunchRef.current());startQLaunch();return off;},[]);
  // W2: efter inloggning hamnar den som har minst en jaktmark på Min jakt; övriga stannar där de var.
  const goMinJaktIfMarks=(opts?:{stayOnInvite?:boolean})=>{
    if(opts?.stayOnInvite)return;
    api.myMarks().then(ms=>{const own=hasOwnMarks(ms,isDemoMark);rememberMinJakt(auth.getUser()?.sub,own);if(own){setAv('marketplace');setMpTab('minajakt');setMpNavTick(t=>t+1);}}).catch(()=>{});
  };
  /** UX12: Min jakt visades för tidigt (ihågkommet) men gäller inte längre – tillbaka till Mer jakt, om inget annat öppnats. */
  const leaveOptimisticMinJakt=()=>{
    if(!optimisticMinJaktRef.current)return;
    optimisticMinJaktRef.current=false;
    if(!getNavState().openMark)setMpTab(t=>t==='minajakt'?'jakt':t);
  };
  /** UX12: appen startas inloggad (ingen djuplänk) – Min jakt om man har en egen jaktmark. */
  const startOnMinJakt=()=>{
    if(!plainStartRef.current)return;
    api.myMarks().then(ms=>{
      const own=hasOwnMarks(ms,isDemoMark);
      rememberMinJakt(auth.getUser()?.sub,own);
      if(own){optimisticMinJaktRef.current=false;setMpTab(t=>t==='jakt'?'minajakt':t);}
      else leaveOptimisticMinJakt();
    }).catch(()=>{});
  };
  const hLogout=()=>{
    const done=()=>{clearWatchCredentials();auth.logout();setUser(null);setIsAdmin(false);rememberMinJakt(null,false);setMpTab(t=>t==='minajakt'?'jakt':t)};
    // Sluta pusha till telefonen medan inloggningen finns kvar — men vänta inte länge på nätet.
    void Promise.race([forgetPushDevice(),new Promise(r=>setTimeout(r,1500))]).finally(done);
  };

  // ── Bottenmeny (iOS och smal webb, inloggad): ersätter toppmenyns flikrad; göms när kartan är i helskärm ──
  const navState=useNavState();
  // CH1: olästa chattmeddelanden (bubblan på Chatt); "Alla chattar" i en gruppchatt visar listan.
  const unreadChats=useUnreadChats(!!user);
  useEffect(()=>{const h=()=>{hNav('chattar');window.scrollTo(0,0);};window.addEventListener(OPEN_CHATS_EVENT,h);return()=>window.removeEventListener(OPEN_CHATS_EVENT,h);},[]); // eslint-disable-line react-hooks/exhaustive-deps
  // Marken stängs när man lämnar marknadsplatsen (VMarketplace avmonteras) – visa toppraden igen.
  useEffect(()=>{if(av!=='marketplace')setMarkOpen(false);},[av]);
  const showBottomNav=mob&&!!user&&!navState.mapFullscreen&&!qOpen;
  const bottomActive:BottomActive=
    av==='chattar'?'chatt':av==='installningar'?'installningar':av==='profil'?'profil':av==='admin'?'admin':
    av!=='marketplace'?null:
    navState.openMark?(navState.openMark.tab==='karta'?'karta':navState.openMark.tab==='gruppchat'?'chatt':'minajakt'):
    mpTab==='minajakt'?'minajakt':mpTab==='jakt'?'jakt':null;
  /** Karta/Chatt: den öppna jaktmarken, annars den senaste, annars den första; utan mark blir det Min jakt. */
  const openMarkTabFromNav=(tab:'karta'|'gruppchat',wantMarkId?:string)=>{
    setShowLogin(false);setAv('marketplace');setMpTab('minajakt');window.scrollTo(0,0);
    const open=getNavState().openMark;
    const mapFullscreen=tab==='karta'; // UX11: bottenmenyns Karta = kartan i helskärm; övriga vägar in öppnar markens startsida
    if(open&&(!wantMarkId||open.id===wantMarkId)){window.dispatchEvent(new CustomEvent(OPEN_MARK_TAB_EVENT,{detail:{markId:open.id,tab,mapFullscreen}}));return;}
    api.myMarks().then(ms=>{
      const markId=wantMarkId&&(ms||[]).some(m=>m.id===wantMarkId)?wantMarkId:pickMarkId(ms||[],isDemoMark,null,lastMarkId());
      if(!markId){setMpNavTick(t=>t+1);return;}
      // VMarketplace öppnar marken när den är laddad (eller direkt om den redan finns).
      try{sessionStorage.setItem('qvenari_open_mark_tab',JSON.stringify({markId,tab,mapFullscreen}));}catch{}
      window.dispatchEvent(new CustomEvent(OPEN_MARK_TAB_EVENT,{detail:{markId,tab,mapFullscreen}}));
    }).catch(()=>setMpNavTick(t=>t+1));
  };
  const goMpTab=(k:'minajakt'|'jakt'|'arrende')=>{hNav('marketplace');setMpTab(k);setMpNavTick(t=>t+1);window.scrollTo(0,0);};

  const rv=()=>{switch(av){
    case'marketplace':return<VMarketplace af={af} onF={setAf} cart={cart} setCart={setCart} user={user} isAdmin={isAdmin} onMer={()=>setMenuOpen(o=>!o)} mpTab={mpTab} setMpTab={setMpTab} onNav={hNav} onLogin={hLogin} joinMeetingId={joinMeetingId} joinMarkId={joinMarkId} joinPendingMark={user?joinPendingMark:null} onJoinPendingMarkConsumed={()=>setJoinPendingMark(null)} huntCartOpen={co} onSetHuntCartOpen={setCo} userAvatarUrl={userAvatarUrl} onMarkOpen={setMarkOpen} navTick={mpNavTick}/>;
    case'dashboard':return<VDashboard/>;case'marks':return<VMarks user={user} isAdmin={isAdmin}/>;
    case'jaktlag':return<VJaktlag/>;case'jakter':return<VJakter/>;
    case'avskjutning':return<VAvskjutning/>;case'mal':return<VMal/>;
    case'profil':return<VProfil onAvatarUpdate={setUserAvatarUrl} user={user} onLogout={hLogout} onOpenSettings={()=>{hNav('installningar');window.scrollTo(0,0);}}/>;
    case'chattar':return<ChatList myEmail={user?.email} onOpen={c=>{if(c.markId)openMarkTabFromNav('gruppchat',c.markId);}}/>;
    case'installningar':return<VInstallningar user={user} onOpenProfile={()=>{hNav('profil');window.scrollTo(0,0);}}/>;
    case'etiketter':return<LabelPrinter user={user} isAdmin={isAdmin}/>;
    case'admin':return<VAdmin isAdmin={isAdmin}/>;default:return null;
  }};

  return(
    <ThemeCtx.Provider value={{mode:themeMode,toggle:toggleTheme}}>
    <ModerationNotice userEmail={user?.email}/>
    <InviteCodeGate onDone={u=>{setUser(u);setShowLogin(false);goMinJaktIfMarks({stayOnInvite:!!inviteMark||inviteNeedsLogin||!!joinMarkId});}}/>
    <div style={{background:T.bg,minHeight:'100vh',fontFamily:FONT_UI,color:T.text,transition:'background 0.3s,color 0.3s'}}>
      <style>{`
        ${themeCssVars(T)}
        :root { --qv-tabs-h: ${showBottomNav?0:NAV_TABS_H}px; --qv-bottom-nav: ${showBottomNav?BOTTOM_NAV_SPACE:'0px'}; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${T.bg}; transition: background 0.3s; }
        :focus-visible { outline: 2px solid ${T.focus}; outline-offset: 2px; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 1px; }
        input, textarea, select { font-family: inherit; }
        button { font-family: inherit; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 767px) {
          /* Toppen räknas från menyns höjd + säkerhetsmarginalen (statusfält/kamerans urtag), annars hamnar sidans början bakom menyn i appen. */
          .view-pad { padding: 16px !important; padding-top: calc(${NAV_MAIN_H + 16}px + env(safe-area-inset-top)) !important; }
          .grid-2col { grid-template-columns: 1fr !important; }
          .grid-3col { grid-template-columns: 1fr !important; }
          .detail-split { grid-template-columns: 1fr !important; }
          table { font-size: 12px; }
          h1 { font-size: 20px !important; }
        }
      `}</style>
      {oauthRequestId&&!showLogin&&<OAuthConsentModal requestId={oauthRequestId} user={user??null} onNeedLogin={()=>setShowLogin(true)} onClose={closeOauth}/>}
      {showLogin&&<LoginModal onClose={()=>{setShowLogin(false);setInviteNeedsLogin(false);}} onLoggedIn={u=>{setUser(u);setShowLogin(false);/* inviteMark stays open after login so user lands on mark page */goMinJaktIfMarks({stayOnInvite:!!inviteMark||inviteNeedsLogin||!!joinMarkId});}}/>}
      {authErr&&<div style={{position:'fixed',bottom:24,right:24,background:themeMode==='dark'?'#2a1a0f':'#fff0f0',border:'1px solid #c0392b',borderRadius:5,padding:'12px 18px',color:'#ff6b6b',fontSize:13,zIndex:9998,maxWidth:360,display:'flex',alignItems:'flex-start',gap:10}}><span style={{fontWeight:700}}>Fel:</span><div style={{flex:1}}>{authErr}</div><button onClick={()=>setAuthErr(null)} style={{background:'none',border:'none',color:'#ff6b6b',cursor:'pointer',fontSize:16,padding:0,lineHeight:1}}>×</button></div>}

      {/* ── Label QR view overlay (ET6: LabelView.tsx) ── */}
      {labelViewId&&<LabelView id={labelViewId} onClose={()=>setLabelViewId(null)}/>}

      {/* ── Invite link → loading spinner ── */}
      {inviteToken&&inviteLoading&&(
        <div style={{position:'fixed',inset:0,background:T.overlay,zIndex:18000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{textAlign:'center'}}>
            <BrandLogo size={32} style={{marginBottom:14}}/>
            <div style={{color:T.text,fontSize:14}}>Laddar inbjudan...</div>
          </div>
        </div>
      )}

      {/* ── Invite link → error ── */}
      {inviteToken&&inviteErr&&(
        <div style={{position:'fixed',inset:0,background:T.overlay,zIndex:18000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>{if(e.target===e.currentTarget){setInviteToken(null);setInviteErr('');}}}>
          <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,width:'100%',maxWidth:420,padding:'32px 28px',textAlign:'center'}}>
            <BrandLogo size={32} style={{marginBottom:16}}/>
            <div style={{color:T.danger||'#c0392b',fontSize:14,marginBottom:20}}>{inviteErr}</div>
            <button onClick={()=>{setInviteToken(null);setInviteErr('');}} style={{...bSc,width:'100%',padding:'11px',fontSize:14}}>Stäng</button>
          </div>
        </div>
      )}

      {/* ── Invite link → mark detail page (where user can see mark, apply, sign contract, pay by card) ── */}
      {inviteMark&&<MarkDetailModal mark={inviteMark} onClose={()=>{setInviteMark(null);setInviteToken(null);}} user={user} isAdmin={isAdmin} onLogin={()=>{setInviteNeedsLogin(true);setShowLogin(true);}} onCalEventCreated={()=>{}}/>}

      {/* ── Join via QR / link overlay — name entry for guests, spinner while joining ── */}
      {!user&&!authLoading&&!joinActiveMeeting&&(joinMeetingId||guestCodeMode)&&(
        <GuestJoin meetingId={joinMeetingId} code={guestCode} name={joinGuestName}
          onJoined={(a,w)=>{void onGuestJoined(a,w);}}
          onClose={()=>{setJoinMeetingId(null);setJoinMarkId(null);setGuestCodeMode(false);setGuestCode(null);window.history.replaceState({},'','/');}}/>
      )}
      {guestNotice&&!joinActiveMeeting&&(
        <div role="alert" style={{position:'fixed',left:16,right:16,bottom:'calc(24px + env(safe-area-inset-bottom, 0px))',zIndex:20001,background:T.surface,color:T.text,border:`1px solid ${T.border}`,borderRadius:RADIUS.lg,boxShadow:T.shadow.raised,padding:'12px 16px',display:'flex',gap:12,alignItems:'center',maxWidth:480,margin:'0 auto',fontFamily:FONT_UI}}>
          <span style={{flex:1,fontSize:14}}>{guestNotice}</span>
          <Button size="sm" variant="ghost" onClick={()=>setGuestNotice('')}>Stäng</Button>
        </div>
      )}
      {user&&joinMeetingId&&!joinActiveMeeting&&!joinMarkId&&(()=>{
        const confirmName=()=>{const n=joinGuestNameInput.trim();if(!n)return;setJoinGuestName(n);setJoinGuestNameConfirmed(true);joinQrAutoRef.current=true;void handleJoin(n);};
        const card:React.CSSProperties={background:T.surface,border:`1px solid ${T.border}`,borderRadius:RADIUS.xl,boxShadow:T.shadow.raised,padding:24,width:'100%',maxWidth:360,textAlign:'center'};
        return(
        <div style={{position:'fixed',inset:0,background:T.bg,color:T.text,fontFamily:FONT_UI,zIndex:20000,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:16,overflowY:'auto'}}>
          <BrandLogo size={40}/>
          <div style={{color:T.textSub,fontSize:15,margin:'10px 0 28px'}}>Du har blivit inbjuden till en jakt</div>
          {/* Gästens namn – visas tills namnet är bekräftat */}
          {!user&&!joinGuestNameConfirmed?(
            <form style={card} onSubmit={e=>{e.preventDefault();confirmName();}}>
              <Field label="Ange ditt namn" style={{textAlign:'left',marginBottom:16}}>
                <Input autoFocus type="text" autoComplete="name" value={joinGuestNameInput} onChange={e=>setJoinGuestNameInput(e.target.value)} placeholder="Ditt namn"/>
              </Field>
              <Button type="submit" block size="lg" disabled={!joinGuestNameInput.trim()} loading={joinBusy}>{joinBusy?'Ansluter…':'Anslut till jakten'}</Button>
            </form>
          ):joinErr?(
            <div style={card}>
              <div role="alert" style={{background:T.dangerSoft,color:T.danger,borderRadius:RADIUS.sm,padding:'10px 14px',fontSize:14,fontWeight:600,marginBottom:16}}>{joinErr}</div>
              <Button block size="lg" loading={joinBusy} onClick={()=>{joinQrAutoRef.current=false;void handleJoin();}}>Försök igen</Button>
              <Button block variant="ghost" style={{marginTop:8}} onClick={()=>{setJoinMeetingId(null);window.history.replaceState({},'',window.location.pathname);}}>Avbryt</Button>
            </div>
          ):(
            <div role="status" style={{display:'flex',flexDirection:'column',alignItems:'center',gap:12}}>
              <span style={{width:32,height:32,border:`3px solid ${T.accentSoft}`,borderTopColor:T.amber,borderRadius:'50%',animation:'spin 0.8s linear infinite',display:'inline-block'}}/>
              <div style={{color:T.textSub,fontSize:14}}>Ansluter{user?` som ${user.name}`:joinGuestName?` som ${joinGuestName}`:''}…</div>
            </div>
          )}
        </div>
        );
      })()}

      {/* ── Active joined meeting — always show KartaTab if joinMarkId exists ── */}
      {joinActiveMeeting&&joinMarkId&&(
        <div style={{position:'fixed',inset:0,zIndex:20000,background:T.bg}}>
          <KartaTab
            mark={joinPendingMark||{id:joinMarkId,name:joinActiveMeeting.title}}
            isAdmin={false}
            token={''}
            onMarkUpdated={async()=>{}}
            huntSession={{
              roomName: joinMeetingId||joinActiveMeeting.title,
              livekitUrl: joinActiveMeeting.livekitUrl,
              livekitToken: joinActiveMeeting.token,
              title: joinActiveMeeting.title,
              meetingId: joinMeetingId||'',
            }}
            userName={user?.name||joinGuestName||undefined}
            userAvatarUrl={userAvatarUrl||undefined}
            onEndHunt={()=>{if(guestCredsRef.current){leaveGuestHunt();return;}setJoinActiveMeeting(null);setJoinMeetingId(null);setJoinPendingMark(null);}}
            onLeaveHunt={()=>{if(guestCredsRef.current){leaveGuestHunt();return;}setJoinActiveMeeting(null);setJoinMeetingId(null);setJoinPendingMark(null);}}
            variant="markDetail"
          />
        </div>
      )}
      {/* ── Fallback: joined meeting without mark (no ?mark= param) ── */}
      {joinActiveMeeting&&!joinMarkId&&(
        <div style={{position:'fixed',inset:0,background:T.bg,fontFamily:FONT_UI,zIndex:20000,display:'flex',flexDirection:'column'}}>
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px 8px 16px',paddingTop:'calc(8px + env(safe-area-inset-top, 0px))',background:T.surface,borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
            <img src="/qvenari-logo.png" alt="Qvenari" style={{height:26,width:'auto'}}/>
            <span style={{color:T.text,fontWeight:700,fontSize:16,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{joinActiveMeeting.title}</span>
            <div style={{flex:1}}/>
            <Button variant="danger" size="sm" onClick={()=>{setJoinActiveMeeting(null);setJoinMeetingId(null);}}>Lämna</Button>
          </div>
          <div style={{flex:1,position:'relative',overflow:'hidden'}}>
            <MapMeeting
              meeting={joinActiveMeeting}
              fullscreen={true}
              onEnd={()=>{setJoinActiveMeeting(null);setJoinMeetingId(null);}}
              onLeaderVideo={setJoinLeaderVideo}
            />
          </div>
        </div>
      )}

      {!markOpen&&<TopNav onNav={hNav} mob={mob} user={user} onLogin={hLogin} cartCount={cart.length} onCartOpen={()=>setCo(true)} avatarUrl={userAvatarUrl} onSettings={user?()=>{hNav('installningar');window.scrollTo(0,0);}:undefined} settingsActive={av==='installningar'} hideMarketplaceCart={av==='marketplace'} isAdmin={isAdmin} onQ={user?openQ:undefined} onHome={()=>{hNav('marketplace');setMpTab('jakt');setMpNavTick(t=>t+1);}} mpTabs={av==='marketplace'?[{k:'minajakt',l:'Min jakt'},{k:'jakt',l:'Mer jakt'},{k:'viltkott',l:'Viltkött ↗'}]:undefined} hideTabs={showBottomNav} mpTab={av==='marketplace'?mpTab:undefined} onMpTab={av==='marketplace'?(k)=>{if(k==='viltkott')window.open(QCARNE_URL,'_blank','noopener');else if(k==='minajakt'&&!user)hLogin();else{setMpTab(k as typeof mpTab);setMpNavTick(t=>t+1);}}:undefined}/>}
      <div style={{minHeight:'100vh',paddingBottom:'var(--qv-bottom-nav)'}}>{rv()}</div>
      {showBottomNav&&<BottomNav active={bottomActive} qOpen={qOpen} onQ={openQ} badges={{chatt:unreadChats}}
        onSelect={k=>{
          if(k==='karta')openMarkTabFromNav('karta');
          // CH2: i en öppen jaktmark går Chatt direkt till markens chatt (där "Alla chattar" leder till listan), annars listan (CH1).
          else if(k==='chatt'){if(av==='marketplace'&&getNavState().openMark)openMarkTabFromNav('gruppchat');else{hNav('chattar');window.scrollTo(0,0);}}
          else goMpTab('minajakt');
        }}
        onMerJakt={()=>goMpTab('jakt')} onViltkott={()=>window.open(QCARNE_URL,'_blank','noopener')} onErbjud={()=>goMpTab('arrende')}
        onProfil={()=>{hNav('profil');window.scrollTo(0,0);}} onAdmin={isAdmin?()=>{hNav('admin');window.scrollTo(0,0);}:undefined}
        onLogout={hLogout}/>}
      {user&&<QPanel open={qOpen} onClose={()=>setQOpen(false)} listenRequest={qListen} user={user} home={Q_CONVERSATION} getContext={deviceContext}/>}

    </div>
    </ThemeCtx.Provider>
  );
}


/** Publik sida för en inbjudan: eget tema (sparat läge eller systemets), samma typsnitt som appen. */
function PublicInvitation({ token }: { token: string }) {
  const [mode, setMode] = useState<ThemeMode>(() => (localStorage.getItem('qvenari_theme') as ThemeMode) || (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'));
  const toggle = useCallback(() => setMode(m => { const n = m === 'dark' ? 'light' : 'dark'; localStorage.setItem('qvenari_theme', n); return n; }), []);
  const T = getTheme(mode);
  return (
    <ThemeCtx.Provider value={{ mode, toggle }}>
      <style>{`${themeCssVars(T)} * { box-sizing: border-box; } body { margin: 0; background: ${T.bg}; font-family: ${FONT_UI}; } @keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <div style={{ fontFamily: FONT_UI }}>
        <InvitationPage token={token} svar={new URLSearchParams(window.location.search).get('svar')} />
      </div>
    </ThemeCtx.Provider>
  );
}

export default function App(){
  // Public vilt page routing (no auth required)
  if (window.location.pathname.startsWith('/vilt/')) {
    const pinId = window.location.pathname.replace('/vilt/', '').split('/')[0];
    if (pinId) { return <ViltPublicPage pinId={pinId} />; }
  }
  // AS1e: integritetspolicy och användarvillkor, utan inloggning (URL:er i App Store Connect)
  const legalDoc = legalDocForPath(window.location.pathname);
  if (legalDoc) return <LegalPage doc={legalDoc} />;
  // AS1c: kontakt och support, utan inloggning (support-URL i App Store Connect)
  if (isSupportPath(window.location.pathname)) return <SupportPage />;
  // P3/KO1/KO2: avtal att signera via länk (mejl), utan inloggning
  const signingToken = signingTokenFromPath(window.location.pathname);
  if (signingToken) return <PublicSigningPage token={signingToken} />;
  // J1: inbjudan till jakt via länk (mejl/sms), utan inloggning
  if (window.location.pathname.startsWith('/inbjudan/')) {
    const token = decodeURIComponent(window.location.pathname.replace('/inbjudan/', '').split('/')[0]);
    if (token) { return <PublicInvitation token={token} />; }
  }
  return <AppInner />;
}
